import { sanitizeHtml } from '../utils/sanitizeHtml';
import React, { useState, useEffect, useMemo } from 'react';
import { CheckCircle, Download, AlertCircle, Calendar, FolderPlus, Shield, FileText, Clock } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { getMyAccountSnapshot } from '../services/firestoreService';
import { db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import AuditTrailViewer from '../components/AuditTrailViewer';
import { getUserNdaTemplate } from '../services/firebase/ndaTemplateService';
import { buildSignerFormDataFromProfile } from '../utils/signerProfilePrefill';
import StartSidebar from '../components/StartSidebar';
import PortalBackground from '../components/PortalBackground';
import '@fontsource/dancing-script';

/** First non-empty trimmed string from candidates */
const pickProfileValue = (...candidates) => {
  for (const v of candidates) {
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return '';
};

const NDA_BUSINESS_STRUCTURE_OPTIONS = [
  'Sole Proprietorship',
  'Partnership',
  'Limited Liability Company',
  'S Corporation',
  'C Corporation',
];

const NDA_FORM_FIELDS = [
  { key: 'signerName', label: 'Full Name' },
  { key: 'signerTitle', label: 'Title' },
  { key: 'companyName', label: 'Company Name' },
  { key: 'businessStructure', label: 'Business Structure' },
  { key: 'companyAddress', label: 'Street' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zipCode', label: 'Zip Code' },
];

/** Map common abbreviations to full business structure labels for the NDA form */
const normalizeBusinessStructure = (raw) => {
  if (!raw || !String(raw).trim()) return '';
  const key = String(raw).trim().toLowerCase();
  const aliases = {
    llc: 'Limited Liability Company',
    'l.l.c.': 'Limited Liability Company',
    'limited liability company (llc)': 'Limited Liability Company',
    's-corp': 'S Corporation',
    's corp': 'S Corporation',
    's corporation': 'S Corporation',
    'c-corp': 'C Corporation',
    'c corp': 'C Corporation',
    'c corporation': 'C Corporation',
    corp: 'C Corporation',
    corporation: 'C Corporation',
  };
  if (aliases[key]) return aliases[key];
  const match = NDA_BUSINESS_STRUCTURE_OPTIONS.find(
    (opt) => opt.toLowerCase() === key
  );
  return match || String(raw).trim();
};

const NDASigningPage = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isOnboardingMode, markStepComplete } = useOnboarding();
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    state: '',
    companyAddress: '',
    city: '',
    zipCode: '',
    businessStructure: '',
    signerName: '',
    signerTitle: '',
    signature: '',
    initial: '',
  });
  const [dynamicFormData, setDynamicFormData] = useState({});
  const [hasAgreed, setHasAgreed] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [showValidation, setShowValidation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isFormComplete = useMemo(
    () =>
      NDA_FORM_FIELDS.every(({ key }) => {
        const value = formData[key];
        return value != null && (typeof value !== 'string' || value.trim() !== '');
      }),
    [formData]
  );
  const [templateData, setTemplateData] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(true);
  const [templateError, setTemplateError] = useState(null);
  
  // E-SIGN Act compliance state
  const [consentData, setConsentData] = useState({
    consentToElectronic: false,
    consentToRecords: false,
    consentTimestamp: null
  });
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [auditTrailData, setAuditTrailData] = useState(null);
  const [highlightedVariableId, setHighlightedVariableId] = useState(null);
  const [currentInputIndex, setCurrentInputIndex] = useState(0);
  const [variablesInOrder, setVariablesInOrder] = useState([]);

  const prefillNdaFormFromProfile = async (user) => {
    const derived = await buildSignerFormDataFromProfile(user);

    setFormData((prev) => ({
      ...prev,
      signerName: prev.signerName || derived.signerName,
      signerTitle: prev.signerTitle || derived.signerTitle,
      companyName: prev.companyName || derived.companyName,
      companyAddress: prev.companyAddress || derived.companyAddress,
      city: prev.city || derived.city,
      state: prev.state || derived.state,
      zipCode: prev.zipCode || derived.zipCode,
      businessStructure: prev.businessStructure || derived.businessStructure,
    }));
  };

  // Load NDA template data on component mount
  useEffect(() => {
    const loadTemplate = async () => {
      if (!currentUser) return;
      
      try {
        setTemplateLoading(true);
        setTemplateError(null);
        const template = await getUserNdaTemplate(currentUser.uid);
        setTemplateData(template);

        await prefillNdaFormFromProfile(currentUser);
      } catch (err) {
        console.error('Error loading NDA template:', err);
        setTemplateError('Failed to load NDA template. Please try again.');
        try {
          await prefillNdaFormFromProfile(currentUser);
        } catch (prefillErr) {
          console.warn('Could not prefill NDA form from profile:', prefillErr);
        }
      } finally {
        setTemplateLoading(false);
      }
    };

    loadTemplate();
  }, [currentUser]);

  // Update variables in order when template data changes
  useEffect(() => {
    if (templateData) {
      const ordered = getClientVariablesInOrder();
      setVariablesInOrder(ordered);
      // Set first variable as highlighted if there are any
      if (ordered.length > 0) {
        setHighlightedVariableId(ordered[0].id);
        setCurrentInputIndex(0);
      }
    }
  }, [templateData]);

  // Auto-scroll to current variable when index changes
  useEffect(() => {
    if (variablesInOrder.length > 0 && currentInputIndex < variablesInOrder.length) {
      const currentVariable = variablesInOrder[currentInputIndex];
      
      // Only highlight and scroll if it's not a consent/agreement checkbox (they don't exist in the document)
      if (!currentVariable.isConsentCheckbox && !currentVariable.isAgreementCheckbox && !currentVariable.isCombinedConsentCheckbox) {
        setHighlightedVariableId(currentVariable.id);
        
        // Scroll to the variable after a short delay to ensure DOM is updated
        setTimeout(() => {
          const variableSpan = document.querySelector(`[data-variable-id="${currentVariable.id}"]`);
          if (variableSpan) {
            variableSpan.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center',
              inline: 'nearest'
            });
          }
        }, 100);
      } else {
        // Clear highlight for consent/agreement checkboxes
        setHighlightedVariableId(null);
      }
    }
  }, [currentInputIndex, variablesInOrder]);

  // Force re-render when dynamic form data changes to update document content
  useEffect(() => {
    // This effect will trigger a re-render when dynamicFormData changes
    // The renderContentWithVariables function will use the updated values
    
    // Sync dynamic form data to legacy formData for backward compatibility
    const clientVariables = getClientVariables();
    
    // Sync initials
    const initialVariable = clientVariables.find(v => v.type === 'initial');
    if (initialVariable && dynamicFormData[initialVariable.id]) {
      setFormData(prev => ({
        ...prev,
        initial: dynamicFormData[initialVariable.id]
      }));
    }
    
    // Sync signature
    const signatureVariable = clientVariables.find(v => v.type === 'signature');
    if (signatureVariable && dynamicFormData[signatureVariable.id]) {
      setFormData(prev => ({
        ...prev,
        signature: dynamicFormData[signatureVariable.id]
      }));
    }
    
    // Sync signer name (look for variables that might contain signer name)
    const signerNameVariable = clientVariables.find(v => 
      v.variableTitle && (
        v.variableTitle.toLowerCase().includes('signer') || 
        (v.variableTitle.toLowerCase().includes('name') && !v.variableTitle.toLowerCase().includes('company'))
      )
    );
    if (signerNameVariable && dynamicFormData[signerNameVariable.id]) {
      setFormData(prev => ({
        ...prev,
        signerName: dynamicFormData[signerNameVariable.id]
      }));
    }
    
    // Sync address (look for variables that contain address)
    const addressVariable = clientVariables.find(v => 
      v.variableTitle && (
        v.variableTitle.toLowerCase() === 'address' ||
        v.variableTitle.toLowerCase().includes('address')
      )
    );
    if (addressVariable && dynamicFormData[addressVariable.id]) {
      setFormData(prev => ({
        ...prev,
        companyAddress: dynamicFormData[addressVariable.id]
      }));
    }
    
    // Sync title (look for variables that contain title)
    const titleVariable = clientVariables.find(v => 
      v.variableTitle && (
        v.variableTitle.toLowerCase() === 'title' ||
        v.variableTitle.toLowerCase().includes('title') ||
        v.variableTitle.toLowerCase().includes('position')
      )
    );
    if (titleVariable && dynamicFormData[titleVariable.id]) {
      setFormData(prev => ({
        ...prev,
        signerTitle: dynamicFormData[titleVariable.id]
      }));
    }
    
  }, [dynamicFormData, templateData]);

  // Extract all variables from template (both Client and BBA)
  const getAllVariables = () => {
    if (!templateData) return [];
    
    const allVariables = [];
    
    // Handle ndaElements format (new format)
    if (templateData.ndaElements && templateData.ndaElements.length > 0) {
      templateData.ndaElements.forEach(element => {
        // Direct input field types
        if (['signature', 'initial', 'text_input', 'checkbox', 'date'].includes(element.type)) {
          allVariables.push({
            id: element.id,
            type: element.type,
            variableTitle: element.variableTitle || element.type,
            content: element.content,
            assignedTo: element.assignedTo,
            isDirect: true
          });
        }
        
        // Variables embedded in text content
        if (element.type === 'text' && element.content) {
          // Extract special variables like [SIGNATURE], [INITIAL], [INITIALS], etc.
          // Use case-insensitive flag to match [Date], [DATE], [Signature], etc.
          const specialPattern = /\[(SIGNATURE|INITIAL|INITIALS|TEXT_INPUT|CHECKBOX|DATE)\]/gi;
          let match;
          while ((match = specialPattern.exec(element.content)) !== null) {
            let variableType = match[1].toLowerCase();
            // Handle INITIALS -> initial mapping
            if (variableType === 'initials') {
              variableType = 'initial';
            }
            
            const newVariable = {
              id: `${element.id}-special-${match.index}`,
              type: variableType,
              variableTitle: variableType === 'text_input' ? 'Text Input' : 
                            variableType === 'checkbox' ? 'Checkbox' :
                            variableType === 'initial' ? 'Initial' :
                            variableType.charAt(0).toUpperCase() + variableType.slice(1),
              content: `[${match[1]}]`,
              assignedTo: element.assignedTo,
              isDirect: false,
              parentElementId: element.id
            };
            allVariables.push(newVariable);
          }
          
          // Extract bracket variables like [Company_Name]
          const bracketPattern = /\[([A-Za-z_][A-Za-z0-9_]*)\]/g;
          while ((match = bracketPattern.exec(element.content)) !== null) {
            const bracketName = match[1];
            const variableType = getVariableTypeFromBracket(bracketName);
            const displayName = bracketName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            
            allVariables.push({
              id: `${element.id}-bracket-${bracketName}-${match.index}`,
              type: variableType,
              variableTitle: displayName,
              content: `[${bracketName}]`,
              assignedTo: element.assignedTo,
              isDirect: false,
              parentElementId: element.id,
              isBracketVariable: true,
              bracketName: bracketName
            });
          }
        }
      });
    }
    
    // Handle original template format (fallback)
    if (templateData.originalTemplate && templateData.originalTemplate.content && templateData.originalTemplate.content.formFields) {
      templateData.originalTemplate.content.formFields.forEach(field => {
        const variableType = getVariableTypeFromBracket(field.id);
        allVariables.push({
          id: `original-${field.id}`,
          type: variableType,
          variableTitle: field.label,
          content: field.variable || `[${field.id}]`,
          assignedTo: 'Client',
          isDirect: true,
          isOriginalTemplate: true,
          originalField: field
        });
      });
    }
    
    return allVariables;
  };

  // Extract all client-assigned variables from template
  const getClientVariables = () => {
    if (!templateData) return [];
    
    const clientVariables = [];
    
    // Handle ndaElements format (new format)
    if (templateData.ndaElements && templateData.ndaElements.length > 0) {
      templateData.ndaElements.forEach(element => {
        // Direct input field types assigned to Client
        if (['signature', 'initial', 'text_input', 'checkbox', 'date'].includes(element.type) && 
            element.assignedTo === 'Client') {
          clientVariables.push({
            id: element.id,
            type: element.type,
            variableTitle: element.variableTitle || element.type,
            content: element.content,
            assignedTo: element.assignedTo,
            isDirect: true
          });
        }
        
        // Variables embedded in text content - if element is assigned to Client or has no assignment (default to Client for bracket variables)
        if (element.type === 'text' && element.content && 
            (element.assignedTo === 'Client' || !element.assignedTo)) {
          // Extract special variables like [SIGNATURE], [INITIAL], [INITIALS], etc.
          // Use case-insensitive flag to match [Date], [DATE], [Signature], etc.
          const specialPattern = /\[(SIGNATURE|INITIAL|INITIALS|TEXT_INPUT|CHECKBOX|DATE)\]/gi;
          let match;
          while ((match = specialPattern.exec(element.content)) !== null) {
            let variableType = match[1].toLowerCase();
            const originalMatch = match[1]; // Preserve original to distinguish INITIAL vs INITIALS
            // Handle INITIALS -> initial mapping
            if (variableType === 'initials') {
              variableType = 'initial';
            }
            // Use element assignment directly - default to Client if no assignment
            let assignedTo = element.assignedTo || 'Client';
            
            // Use original match to set title - 'Initial' for [INITIAL], 'Initials' for [INITIALS]
            let variableTitle;
            if (originalMatch.toUpperCase() === 'INITIALS') {
              variableTitle = 'Initials';
            } else if (originalMatch.toUpperCase() === 'INITIAL') {
              variableTitle = 'Initial';
            } else {
              variableTitle = variableType === 'text_input' ? 'Text Input' : 
                            variableType === 'checkbox' ? 'Checkbox' :
                            variableType.charAt(0).toUpperCase() + variableType.slice(1);
            }
            
            const newVariable = {
              id: `${element.id}-special-${match.index}`,
              type: variableType,
              variableTitle: variableTitle,
              content: `[${match[1]}]`,
              originalContent: originalMatch, // Store original to distinguish INITIAL vs INITIALS
              assignedTo: assignedTo,
              isDirect: false,
              parentElementId: element.id
            };
            clientVariables.push(newVariable);
          }
          
          // Extract bracket variables like [Company_Name] - only for Client-assigned elements
          // EXCLUDE system variables like [Contract_Number], [Program_Name], etc.
          // EXCLUDE BBA variables like [BBA_Name], [BBA_Title], etc. (they start with BBA_ prefix)
          const bracketPattern = /\[([A-Za-z_][A-Za-z0-9_]*)\]/g;
          while ((match = bracketPattern.exec(element.content)) !== null) {
            const bracketName = match[1];
            
            // Skip system variables - these should not be client input fields
            if (['Contract_Number', 'Program_Name', 'Program_Items', 'Monthly_Price', 'Start_Date'].includes(bracketName)) {
              continue;
            }
            
            // Skip BBA variables - these start with BBA_ prefix and should not be client input fields
            if (bracketName.startsWith('BBA_')) {
              continue;
            }
            
            const variableType = getVariableTypeFromBracket(bracketName);
            const displayName = bracketName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            
            // Use element assignment directly - default to Client if no assignment
            let assignedTo = element.assignedTo || 'Client';
            
            clientVariables.push({
              id: `${element.id}-bracket-${bracketName}-${match.index}`,
              type: variableType,
              variableTitle: displayName,
              content: `[${bracketName}]`,
              assignedTo: assignedTo,
              isDirect: false,
              parentElementId: element.id,
              isBracketVariable: true,
              bracketName: bracketName
            });
          }
        }
      });
    }
    
    // Handle original template format (fallback)
    if (templateData.originalTemplate && templateData.originalTemplate.content && templateData.originalTemplate.content.formFields) {
      templateData.originalTemplate.content.formFields.forEach(field => {
        const variableType = getVariableTypeFromBracket(field.id);
        clientVariables.push({
          id: `original-${field.id}`,
          type: variableType,
          variableTitle: field.label,
          content: field.variable || `[${field.id}]`,
          assignedTo: 'Client',
          isDirect: true,
          isOriginalTemplate: true,
          originalField: field
        });
      });
    }
    
    // Filter to only return Client-assigned variables
    const clientOnlyVariables = clientVariables.filter(v => v.assignedTo === 'Client');
    
    return clientOnlyVariables;
  };

  // Get client variables in document order (as they appear in the template)
  const getClientVariablesInOrder = () => {
    if (!templateData) return [];
    
    let orderedVariables = [];
    const seenVariableIds = new Set();
    
    // Handle ndaElements format (new format)
    if (templateData.ndaElements && templateData.ndaElements.length > 0) {
      templateData.ndaElements.forEach(element => {
        // Direct input field types assigned to Client
        if (['signature', 'initial', 'text_input', 'checkbox', 'date'].includes(element.type) && 
            element.assignedTo === 'Client') {
          if (!seenVariableIds.has(element.id)) {
            orderedVariables.push({
              id: element.id,
              type: element.type,
              variableTitle: element.variableTitle || element.type,
              content: element.content,
              assignedTo: element.assignedTo,
              isDirect: true,
              elementIndex: templateData.ndaElements.indexOf(element)
            });
            seenVariableIds.add(element.id);
          }
        }
        
        // Variables embedded in text content
        if (element.type === 'text' && element.content && 
            (element.assignedTo === 'Client' || !element.assignedTo)) {
          const elementIndex = templateData.ndaElements.indexOf(element);
          
          // Extract special variables like [SIGNATURE], [INITIAL], [INITIALS], etc.
          // Use case-insensitive flag to match [Date], [DATE], [Signature], etc.
          const specialPattern = /\[(SIGNATURE|INITIAL|INITIALS|TEXT_INPUT|CHECKBOX|DATE)\]/gi;
          let match;
          let matchIndex = 0;
          while ((match = specialPattern.exec(element.content)) !== null) {
            let variableType = match[1].toLowerCase();
            const originalMatch = match[1]; // Preserve original to distinguish INITIAL vs INITIALS
            if (variableType === 'initials') {
              variableType = 'initial'; // Normalize type for processing
            }
            let assignedTo = element.assignedTo || 'Client';
            
            const variableId = `${element.id}-special-${matchIndex}`;
            if (!seenVariableIds.has(variableId) && assignedTo === 'Client') {
              // Use original match to set title - 'Initial' for [INITIAL], 'Initials' for [INITIALS]
              let variableTitle;
              if (originalMatch.toUpperCase() === 'INITIALS') {
                variableTitle = 'Initials';
              } else if (originalMatch.toUpperCase() === 'INITIAL') {
                variableTitle = 'Initial';
              } else {
                variableTitle = variableType === 'text_input' ? 'Text Input' : 
                              variableType === 'checkbox' ? 'Checkbox' :
                              variableType.charAt(0).toUpperCase() + variableType.slice(1);
              }
              
              orderedVariables.push({
                id: variableId,
                type: variableType,
                variableTitle: variableTitle,
                content: `[${match[1]}]`,
                originalContent: originalMatch, // Store original to distinguish INITIAL vs INITIALS
                assignedTo: assignedTo,
                isDirect: false,
                parentElementId: element.id,
                elementIndex: elementIndex,
                matchIndex: match.index
              });
              seenVariableIds.add(variableId);
            }
            matchIndex++;
          }
          
          // Extract bracket variables like [Company_Name]
          // EXCLUDE BBA variables like [BBA_Name], [BBA_Title], etc. (they start with BBA_ prefix)
          const bracketPattern = /\[([A-Za-z_][A-Za-z0-9_]*)\]/g;
          matchIndex = 0;
          while ((match = bracketPattern.exec(element.content)) !== null) {
            const bracketName = match[1];
            
            // Skip system variables
            if (['Contract_Number', 'Program_Name', 'Program_Items', 'Monthly_Price', 'Start_Date'].includes(bracketName)) {
              continue;
            }
            
            // Skip BBA variables - these start with BBA_ prefix and should not be client input fields
            if (bracketName.startsWith('BBA_')) {
              continue;
            }
            
            const variableType = getVariableTypeFromBracket(bracketName);
            const displayName = bracketName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            let assignedTo = element.assignedTo || 'Client';
            
            const variableId = `${element.id}-bracket-${bracketName}-${matchIndex}`;
            if (!seenVariableIds.has(variableId) && assignedTo === 'Client') {
              orderedVariables.push({
                id: variableId,
                type: variableType,
                variableTitle: displayName,
                content: `[${bracketName}]`,
                assignedTo: assignedTo,
                isDirect: false,
                parentElementId: element.id,
                isBracketVariable: true,
                bracketName: bracketName,
                elementIndex: elementIndex,
                matchIndex: match.index
              });
              seenVariableIds.add(variableId);
            }
            matchIndex++;
          }
        }
      });
    }
    
    // Handle original template format (fallback)
    if (templateData.originalTemplate && templateData.originalTemplate.content && templateData.originalTemplate.content.formFields) {
      templateData.originalTemplate.content.formFields.forEach((field, index) => {
        const variableType = getVariableTypeFromBracket(field.id);
        const variableId = `original-${field.id}`;
        if (!seenVariableIds.has(variableId)) {
          orderedVariables.push({
            id: variableId,
            type: variableType,
            variableTitle: field.label,
            content: field.variable || `[${field.id}]`,
            assignedTo: 'Client',
            isDirect: true,
            isOriginalTemplate: true,
            originalField: field,
            elementIndex: index
          });
          seenVariableIds.add(variableId);
        }
      });
    }
    
    // Sort by elementIndex and matchIndex to maintain document order
    orderedVariables.sort((a, b) => {
      if (a.elementIndex !== b.elementIndex) {
        return a.elementIndex - b.elementIndex;
      }
      return (a.matchIndex || 0) - (b.matchIndex || 0);
    });
    
    // Filter out duplicate variables - keep only the first occurrence of each unique variable
    // Track which variables we've seen by their normalized name/type combination
    const seenVariables = new Set();
    orderedVariables = orderedVariables.filter(variable => {
      // For date variables, keep only the first one
      if (variable.type === 'date') {
        if (seenVariables.has('date')) {
          return false; // Remove subsequent date variables
        }
        seenVariables.add('date');
        return true; // Keep the first date variable
      }
      
      // For signature variables, keep only the first one
      if (variable.type === 'signature') {
        if (seenVariables.has('signature')) {
          return false; // Remove subsequent signature variables
        }
        seenVariables.add('signature');
        return true; // Keep the first signature variable
      }
      
      // For 'initial' type variables, check by variableTitle first to catch duplicates
      // This must happen before bracket variable check to catch duplicates across both types
      if (variable.type === 'initial' && variable.variableTitle) {
        const normalizedTitle = variable.variableTitle.toLowerCase().trim();
        const titleKey = `${variable.type}-${normalizedTitle}`;
        if (seenVariables.has(titleKey)) {
          return false; // Remove subsequent instances with the same title
        }
        seenVariables.add(titleKey);
        return true; // Keep the first instance
      }
      
      // For bracket variables, use bracketName to identify duplicates
      if (variable.isBracketVariable && variable.bracketName) {
        const key = `bracket-${variable.bracketName}`;
        if (seenVariables.has(key)) {
          return false; // Remove subsequent instances of the same bracket variable
        }
        seenVariables.add(key);
        return true; // Keep the first instance
      }
      
      // For other variables, use variableTitle to identify duplicates
      // But preserve distinction between 'Initial' and 'Initials' (they're different variables)
      if (variable.variableTitle) {
        const normalizedTitle = variable.variableTitle.toLowerCase().trim();
        // For other types, use originalContent if available to distinguish variants
        const key = variable.originalContent 
          ? `${variable.type}-${variable.originalContent.toUpperCase()}` 
          : `${variable.type}-${normalizedTitle}`;
        if (seenVariables.has(key)) {
          return false; // Remove subsequent instances with the same key
        }
        seenVariables.add(key);
        return true; // Keep the first instance
      }
      
      // Keep variables that don't match any duplicate pattern
      return true;
    });
    
    // Add E-SIGN Act consent checkboxes and agreement checkbox as a combined variable
    // Insert after the 16th variable, or at the end if there are fewer than 16 variables
    const insertIndex = orderedVariables.length >= 16 ? 16 : orderedVariables.length;
    
    // Add combined E-SIGN Act consent and agreement checkboxes as a single variable
    orderedVariables.splice(insertIndex, 0, {
      id: 'e-sign-consent-and-agreement',
      type: 'combined_consent_checkbox',
      variableTitle: 'Electronic Signature Consent (E-SIGN Act Required)',
      content: '',
      assignedTo: 'Client',
      isDirect: true,
      isCombinedConsentCheckbox: true,
      elementIndex: 9999,
      matchIndex: 0
    });
    
    return orderedVariables;
  };

  // Get variable type from bracket name
  const getVariableTypeFromBracket = (bracketName) => {
    const lowerName = bracketName.toLowerCase();
    if (lowerName.includes('signature') || lowerName.includes('sign')) return 'signature';
    if (lowerName.includes('initial')) return 'initial';
    if (lowerName.includes('date')) return 'date';
    if (lowerName.includes('check') || lowerName.includes('agree')) return 'checkbox';
    return 'text_input'; // Default to text input
  };

  // Clean placeholder text by removing brackets and underscores
  const cleanPlaceholderText = (content) => {
    if (!content) return '';
    
    // Remove brackets and clean up the text
    let cleaned = content
      .replace(/\[|\]/g, '') // Remove brackets
      .replace(/_/g, ' ') // Replace underscores with spaces
      .trim();
    
    // Capitalize first letter of each word
    cleaned = cleaned.replace(/\b\w/g, l => l.toUpperCase());
    
    return cleaned;
  };

  // Convert bracket notation to user-friendly display placeholder
  const getUserFriendlyPlaceholder = (bracketNotation) => {
    if (!bracketNotation) return '';
    
    // Remove brackets
    let cleaned = bracketNotation.replace(/\[|\]/g, '');
    
    // Convert to readable format
    cleaned = cleaned
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize first letter of each word
    
    // Create an underlined placeholder that looks like a form field
    // Use a span with border-bottom to create an underline effect
    // Make it look like a blank line where text will be filled in
    const placeholderLength = Math.max(cleaned.length * 8, 120); // Calculate width based on text length
    return `<span class="inline-block border-b-2 border-dashed border-gray-400 dark:border-gray-500 px-1 text-gray-500 dark:text-gray-400 italic" style="min-width: ${placeholderLength}px;">${cleaned}</span>`;
  };

  // Get enhanced form data that includes both formData and dynamicFormData
  const getEnhancedFormData = () => {
    const enhancedFormData = { ...formData };
    const clientVariables = getClientVariables();
    
    // Map all dynamic form data to the appropriate legacy form fields
    clientVariables.forEach(variable => {
      const value = dynamicFormData[variable.id];
      if (value && (typeof value !== 'string' || value.trim())) {
        const title = variable.variableTitle.toLowerCase();
        
        // Map based on variable title content and type
        let mapped = false;
        
        // Check by type first (most reliable)
        if (variable.type === 'signature') {
          enhancedFormData.signature = value;
          mapped = true;
        } else if (variable.type === 'initial') {
          enhancedFormData.initial = value;
          mapped = true;
        }
        
        // Then check by title content
        if (!mapped) {
          if (title.includes('signer') && title.includes('name')) {
            enhancedFormData.signerName = value;
            mapped = true;
          } else if (title.includes('signer') && title.includes('title')) {
            enhancedFormData.signerTitle = value;
            mapped = true;
          } else if (title.includes('company') && title.includes('name')) {
            enhancedFormData.companyName = value;
            mapped = true;
          } else if (title.includes('company') && title.includes('address')) {
            enhancedFormData.companyAddress = value;
            mapped = true;
          } else if (title.includes('city')) {
            enhancedFormData.city = value;
            mapped = true;
          } else if (title.includes('state')) {
            enhancedFormData.state = value;
            mapped = true;
          } else if (title.includes('zip')) {
            enhancedFormData.zipCode = value;
            mapped = true;
          } else if (title.includes('business') && title.includes('structure')) {
            enhancedFormData.businessStructure = value;
            mapped = true;
          }
        }
        
        // Additional fallback mappings for common patterns
        if (!mapped) {
          if (title.includes('name') && !title.includes('company')) {
            // If it's a name field but not company name, likely signer name
            enhancedFormData.signerName = value;
            mapped = true;
          } else if (title === 'address' || title.includes('address')) {
            // Map "Address" or any address field to companyAddress
            enhancedFormData.companyAddress = value;
            mapped = true;
          } else if (title === 'title' || title.includes('title') || title.includes('position')) {
            // Map "Title" or any title field to signerTitle
            enhancedFormData.signerTitle = value;
            mapped = true;
          }
        }
      }
    });
    
    // Fallback to legacy form data for any fields that weren't found in dynamic data
    // This ensures backward compatibility
    if (!enhancedFormData.signerName && formData.signerName) {
      enhancedFormData.signerName = formData.signerName;
    }
    if (!enhancedFormData.signerTitle && formData.signerTitle) {
      enhancedFormData.signerTitle = formData.signerTitle;
    }
    if (!enhancedFormData.companyName && formData.companyName) {
      enhancedFormData.companyName = formData.companyName;
    }
    if (!enhancedFormData.companyAddress && formData.companyAddress) {
      enhancedFormData.companyAddress = formData.companyAddress;
    }
    if (!enhancedFormData.city && formData.city) {
      enhancedFormData.city = formData.city;
    }
    if (!enhancedFormData.state && formData.state) {
      enhancedFormData.state = formData.state;
    }
    if (!enhancedFormData.zipCode && formData.zipCode) {
      enhancedFormData.zipCode = formData.zipCode;
    }
    if (!enhancedFormData.businessStructure && formData.businessStructure) {
      enhancedFormData.businessStructure = formData.businessStructure;
    }
    if (!enhancedFormData.signature && formData.signature) {
      enhancedFormData.signature = formData.signature;
    }
    if (!enhancedFormData.initial && formData.initial) {
      enhancedFormData.initial = formData.initial;
    }
    
    return enhancedFormData;
  };

  // Initialize form data based on template variables
  const initializeFormDataFromTemplate = (ndaElements) => {
    const newFormData = { ...formData };
    
    ndaElements.forEach(element => {
      if (element.type === 'text' && element.content) {
        // Extract bracket variables from content
        const bracketPattern = /\[([A-Za-z_][A-Za-z0-9_]*)\]/g;
        let match;
        while ((match = bracketPattern.exec(element.content)) !== null) {
          const variableName = match[1].toLowerCase();
          const fieldName = variableName.replace(/_/g, '');
          
          // Map common variable names to form fields
          if (fieldName.includes('companyname') || fieldName.includes('company')) {
            newFormData.companyName = '';
          } else if (fieldName.includes('state')) {
            newFormData.state = '';
          } else if (fieldName.includes('address')) {
            newFormData.companyAddress = '';
          } else if (fieldName.includes('city')) {
            newFormData.city = '';
          } else if (fieldName.includes('zip') || fieldName.includes('zipcode')) {
            newFormData.zipCode = '';
          } else if (fieldName.includes('businessstructure') || fieldName.includes('structure')) {
            newFormData.businessStructure = '';
          } else if (fieldName.includes('signername') || fieldName.includes('signer')) {
            newFormData.signerName = '';
          } else if (fieldName.includes('title')) {
            newFormData.signerTitle = '';
          } else if (fieldName.includes('signature')) {
            newFormData.signature = '';
          } else if (fieldName.includes('initial')) {
            newFormData.initial = '';
          }
        }
      }
    });
    
    setFormData(newFormData);
  };

  // Initialize form data from original template format
  const initializeFormDataFromOriginalTemplate = (formFields) => {
    const newFormData = { ...formData };
    
    formFields.forEach(field => {
      const fieldId = field.id;
      if (fieldId === 'companyName') {
        newFormData.companyName = '';
      } else if (fieldId === 'state') {
        newFormData.state = '';
      } else if (fieldId === 'companyAddress') {
        newFormData.companyAddress = '';
      } else if (fieldId === 'city') {
        newFormData.city = '';
      } else if (fieldId === 'zipCode') {
        newFormData.zipCode = '';
      } else if (fieldId === 'signerName') {
        newFormData.signerName = '';
      } else if (fieldId === 'signerTitle') {
        newFormData.signerTitle = '';
      } else if (fieldId === 'signature') {
        newFormData.signature = '';
      }
    });
    
    setFormData(newFormData);
  };

  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
    if (bottom) {
      setIsScrolledToBottom(true);
    }
  };

  // Scroll to and highlight a variable in the document
  const scrollToVariable = (variable) => {
    const ndaContainer = document.querySelector('.overflow-y-auto');
    if (!ndaContainer) return;

    // Set the highlighted variable ID to trigger re-render with highlight
    setHighlightedVariableId(variable.id);
    
    // Find the element containing this variable
    let targetElement = null;
    
    if (variable.parentElementId) {
      // Find the parent element
      targetElement = ndaContainer.querySelector(`[data-element-id="${variable.parentElementId}"]`);
    }
    
    // If not found, try to find by variable ID or bracket name
    if (!targetElement) {
      if (variable.isBracketVariable && variable.bracketName) {
        // Try to find element containing this bracket variable
        const allElements = ndaContainer.querySelectorAll('[data-element-id]');
        for (const el of allElements) {
          const content = el.textContent || el.innerText;
          if (content.includes(`[${variable.bracketName}]`)) {
            targetElement = el;
            break;
          }
        }
      }
    }
    
    // If still not found, try to find by variable type and content
    if (!targetElement) {
      const allElements = ndaContainer.querySelectorAll('[data-element-id]');
      for (const el of allElements) {
        const content = el.textContent || el.innerText;
        // Check for special variables like [SIGNATURE], [INITIAL], etc.
        if (variable.type === 'signature' && (content.includes('[SIGNATURE]') || content.toLowerCase().includes('signature'))) {
          targetElement = el;
          break;
        } else if (variable.type === 'initial' && (content.includes('[INITIAL]') || content.includes('[INITIALS]') || content.toLowerCase().includes('initial'))) {
          targetElement = el;
          break;
        } else if (variable.type === 'date' && (content.includes('[DATE]') || content.includes('[Date]') || content.toLowerCase().includes('[date]'))) {
          targetElement = el;
          break;
        } else if (variable.content && content.includes(variable.content)) {
          targetElement = el;
          break;
        }
      }
    }
    
    // If still not found, try searching by variable title in the content
    if (!targetElement) {
      const allElements = ndaContainer.querySelectorAll('[data-element-id]');
      const searchTerms = variable.variableTitle.toLowerCase().split(' ');
      for (const el of allElements) {
        const content = el.textContent || el.innerText;
        const lowerContent = content.toLowerCase();
        // Check if any search term appears in the content
        if (searchTerms.some(term => term.length > 2 && lowerContent.includes(term))) {
          targetElement = el;
          break;
        }
      }
    }
    
    if (targetElement) {
      // Wait a bit for the highlight span to be rendered, then scroll to it
      setTimeout(() => {
        // Try to find the specific variable span
        const variableSpan = ndaContainer.querySelector(`[data-variable-id="${variable.id}"]`);
        if (variableSpan) {
          variableSpan.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });
        } else {
          // Fallback to scrolling to the parent element
          targetElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });
        }
      }, 100);
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
    
    // Scroll to relevant section based on field being edited
    const ndaContainer = document.querySelector('.overflow-y-auto');
    if (ndaContainer) {
      if (id === 'companyName' || id === 'state' || id === 'companyAddress') {
        const section = document.getElementById('company-name-section');
        if (section) section.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (id === 'signerName' || id === 'signerTitle' || id === 'signature') {
        const section = document.getElementById('signature-section');
        if (section) section.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const handleSignatureChange = (e) => {
    setFormData(prev => ({ ...prev, signature: e.target.value }));
    
    // Scroll to signature section
    const ndaContainer = document.querySelector('.overflow-y-auto');
    if (ndaContainer) {
      const section = document.getElementById('signature-section');
      if (section) section.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleCheckboxChange = () => {
    setHasAgreed(!hasAgreed);
  };

  const handleConsentChange = (type) => {
    setConsentData(prev => ({
      ...prev,
      [type]: !prev[type],
      consentTimestamp: new Date().toISOString()
    }));
  };

  const handleShowConsentModal = () => {
    setShowConsentModal(true);
  };

  // Populate user profile data from NDA submission
  const populateProfileFromNDA = async (formData) => {
    try {
      console.log('Populating profile data from NDA submission:', formData);
      
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Map NDA data to profile fields
        const profileUpdates = {};
        
        // Map signer name to firstName and lastName
        if (formData.signerName && formData.signerName.trim()) {
          const cleanName = formData.signerName.trim();
          // Only update if the name is meaningful (not just single characters like "d")
          if (cleanName.length > 1 && cleanName !== 'd' && cleanName !== 'D') {
            const nameParts = cleanName.split(/\s+/); // Split on any whitespace
            if (nameParts.length >= 1) {
              profileUpdates.firstName = nameParts[0];
            }
            if (nameParts.length >= 2) {
              profileUpdates.lastName = nameParts.slice(1).join(' ');
            }
          }
        }
        
        // Map company name
        if (formData.companyName && formData.companyName.trim()) {
          const cleanCompany = formData.companyName.trim();
          // Only update if the company name is meaningful (not just single characters like "d")
          if (cleanCompany.length > 1 && cleanCompany !== 'd' && cleanCompany !== 'D') {
            profileUpdates.company = cleanCompany;
          }
        }
        
        // Map address information
        const hasValidAddress = 
          (formData.companyAddress && formData.companyAddress.trim() && formData.companyAddress.trim() !== 'd') ||
          (formData.city && formData.city.trim() && formData.city.trim() !== 'd') ||
          (formData.state && formData.state.trim() && formData.state.trim() !== 'd') ||
          (formData.zipCode && formData.zipCode.trim() && formData.zipCode.trim() !== 'd');
        
        if (hasValidAddress) {
          profileUpdates.address = {
            ...userData.address, // Preserve existing address data
            street: (formData.companyAddress?.trim() && formData.companyAddress.trim() !== 'd') 
              ? formData.companyAddress.trim() 
              : userData.address?.street || '',
            city: (formData.city?.trim() && formData.city.trim() !== 'd') 
              ? formData.city.trim() 
              : userData.address?.city || '',
            state: (formData.state?.trim() && formData.state.trim() !== 'd') 
              ? formData.state.trim() 
              : userData.address?.state || '',
            zipCode: (formData.zipCode?.trim() && formData.zipCode.trim() !== 'd') 
              ? formData.zipCode.trim() 
              : userData.address?.zipCode || '',
            country: userData.address?.country || 'United States' // Preserve existing country
          };
        }
        
        // Only update if we have meaningful data to update
        if (Object.keys(profileUpdates).length > 0) {
          await updateDoc(userDocRef, {
            ...profileUpdates,
            profileLastUpdatedFromNDA: new Date().toISOString(),
            lastUpdated: new Date().toISOString()
          });
          
          console.log('Profile data updated from NDA submission:', profileUpdates);
        } else {
          console.log('No meaningful profile data to update from NDA submission');
        }
      }
    } catch (error) {
      console.error('Error populating profile from NDA:', error);
      // Don't throw error here as the main NDA signing should still complete
    }
  };

  // Save NDA data to user's document and try to update lead account
  const saveNDAToUserAndLead = async (signedDocument, formData) => {
    try {
      console.log('Saving NDA data for user:', currentUser.email);
      console.log('saveNDAToUserAndLead - received formData:', formData);
      console.log('saveNDAToUserAndLead - formData.companyAddress:', formData.companyAddress);
      console.log('saveNDAToUserAndLead - formData.signerTitle:', formData.signerTitle);
      
      // First, populate user profile data from NDA submission
      await populateProfileFromNDA(formData);
      
      // Then, save NDA data to the user's document
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const existingCollection = userData.collection || [];
        
        // Check if NDA already exists in collection
        const existingNDAIndex = existingCollection.findIndex(item => item.isSignedNDA);
        
        const ndaData = {
          documentId: signedDocument.id,
          signerName: formData.signerName,
          signerTitle: formData.signerTitle,
          companyName: formData.companyName,
          companyAddress: formData.companyAddress,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          businessStructure: formData.businessStructure,
          signature: formData.signature,
          initial: formData.initial || '',
          signedAt: signedDocument.signedAt,
          eSignCompliance: signedDocument.eSignCompliance,
          documentContent: signedDocument.eSignCompliance.recordIntegrity.documentSnapshot.documentContent,
          // Include template elements for PDF generation with page breaks
          templateElements: templateData?.ndaElements || []
        };
        
        if (existingNDAIndex >= 0) {
          // Update existing NDA
          existingCollection[existingNDAIndex] = {
            ...existingCollection[existingNDAIndex],
            ...ndaData
          };
        } else {
          // Add new NDA to collection
          existingCollection.push({
            ...signedDocument,
            ...ndaData
          });
        }
        
        // Update user document with NDA data
        await updateDoc(userDocRef, {
          collection: existingCollection,
          signedNDA: ndaData, // Also store separately for easy access
          lastUpdated: new Date().toISOString()
        });
        
        console.log('NDA data saved to user document');
      }
      
      // Now try to find and update the lead account
      console.log('Attempting to save NDA to lead account for user:', currentUser.email);
      
      // Find the lead account associated with this user
      let accountsSnapshot = await getMyAccountSnapshot('Leads');
      
      if (!accountsSnapshot.empty) {
        const leadDoc = accountsSnapshot.docs[0];
        const leadData = leadDoc.data();
        
        console.log('Updating lead account:', leadDoc.id, 'with data:', leadData);
        
        // Prepare NDA data and clean undefined values
        const ndaData = {
          documentId: signedDocument.id,
          signerName: formData.signerName || '',
          signerTitle: formData.signerTitle || '',
          companyName: formData.companyName || '',
          companyAddress: formData.companyAddress || '',
          city: formData.city || '',
          state: formData.state || '',
          zipCode: formData.zipCode || '',
          businessStructure: formData.businessStructure || '',
          signature: formData.signature || '',
          initial: formData.initial || '',
          signedAt: signedDocument.signedAt || new Date().toISOString(),
          eSignCompliance: signedDocument.eSignCompliance || {},
          documentContent: signedDocument.eSignCompliance?.recordIntegrity?.documentSnapshot?.documentContent || 'Document content not available',
          // Include template elements for PDF generation with page breaks
          templateElements: templateData?.ndaElements || []
        };
        
        console.log('Raw NDA data before cleaning:', ndaData);
        
        // Clean the NDA data to remove undefined values
        const cleanNDAData = Object.fromEntries(
          Object.entries(ndaData).filter(([key, value]) => value !== undefined)
        );
        
        console.log('Saving clean NDA data to lead account:', cleanNDAData);
        
        // Update the lead with NDA information and substatus
        const currentDate = new Date().toISOString();
        const updatedData = {
          ndaStatus: 'Completed',
          ndaLastUpdated: currentDate,
          signedNDA: cleanNDAData,
          lastUpdated: currentDate
        };
        
        // Update substatus to "NDA Signed" if in Assessment stage
        if (leadData.stage === 'Assessment') {
          updatedData.substatus = 'NDA Signed';
          
          // Track substatus dates
          const substatusDates = leadData.substatusDates || {};
          if (!substatusDates['NDA Signed']) {
            substatusDates['NDA Signed'] = currentDate;
          }
          updatedData.substatusDates = substatusDates;
          
          // Add to status history
          const statusHistory = leadData.statusHistory || [];
          const statusEntry = {
            status: 'Assessment',
            substatus: 'NDA Signed',
            previousStatus: leadData.stage || 'Unknown',
            previousSubstatus: leadData.substatus || null,
            date: currentDate,
            updatedBy: 'system',
            action: 'NDA signed - substatus updated automatically'
          };
          updatedData.statusHistory = [...statusHistory, statusEntry];
        }
        
        await updateDoc(doc(db, 'accounts', leadDoc.id), updatedData);
        
        console.log('NDA data successfully saved to lead account:', leadDoc.id);
      } else {
        console.log('No lead account found for user:', currentUser.email);
        console.log('NDA data saved to user document only. Lead account will be updated when created.');
      }
    } catch (error) {
      console.error('Error saving NDA data:', error);
      // Don't throw error here as the main NDA signing should still complete
    }
  };

  const validateForm = () => {
    if (!templateData || !templateData.ndaElements) {
      setError('No NDA template found. Please create a template first.');
      return false;
    }

    // Require the user to confirm they have read the NDA
    if (!hasAgreed) {
      setError('Please confirm that you have read and agree to the terms of the NDA.');
      return false;
    }

    // Check that all non-consent client variables are filled
    const emptyVariables = variablesInOrder.filter(variable => {
      // The combined consent/agreement step is handled by hasAgreed above
      if (variable.isCombinedConsentCheckbox || variable.isConsentCheckbox || variable.isAgreementCheckbox) {
        return false;
      }
      const value = dynamicFormData[variable.id];
      return !value || (typeof value === 'string' && !value.trim());
    });

    if (emptyVariables.length > 0) {
      setError(`Please fill out all required fields. Missing: ${emptyVariables.map(v => v.variableTitle).join(', ')}`);
      return false;
    }

    return true;
  };

  const validateNdaForm = () => {
    const errors = {};
    NDA_FORM_FIELDS.forEach(({ key, label }) => {
      const value = formData[key];
      if (value == null || (typeof value === 'string' && !value.trim())) {
        errors[key] = `${label} is required`;
      }
    });
    return {
      errors,
      isValid: Object.keys(errors).length === 0,
    };
  };

  const clearFieldError = (key) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const ndaInputClass = (key) => {
    const invalid = showValidation && Boolean(fieldErrors[key]);
    const base = 'w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2';
    if (invalid) {
      return `${base} border-red-500 focus:ring-red-500 ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`;
    }
    return `${base} focus:ring-blue-500 ${darkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'}`;
  };

  const applyValidationFeedback = (errors) => {
    setFieldErrors(errors);

    const missingLabels = NDA_FORM_FIELDS.filter(({ key }) => errors[key]).map(({ label }) => label);
    const parts = [];
    if (missingLabels.length > 0) {
      parts.push(`Please complete: ${missingLabels.join(', ')}.`);
    }
    setError(parts.join(' '));

    const firstInvalidKey = NDA_FORM_FIELDS.find(({ key }) => errors[key])?.key;
    if (firstInvalidKey) {
      const el = document.getElementById(`nda-${firstInvalidKey}`);
      el?.focus();
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleEarlySignClick = () => {
    setShowValidation(true);
    const { errors } = validateNdaForm();
    applyValidationFeedback(errors);
  };

  useEffect(() => {
    if (isFormComplete) {
      setShowValidation(false);
      setFieldErrors({});
      setError('');
    }
  }, [isFormComplete]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormComplete) return;

    try {
      setIsSubmitting(true);
      setError('');
      setFieldErrors({});
      setShowValidation(false);

      // Call the backend to create a DocuSign envelope and get an embedded signing URL
      const createEnvelope = httpsCallable(functions, 'createNdaEnvelope');
      const result = await createEnvelope({
        formData: {
          signerName: formData.signerName,
          signerTitle: formData.signerTitle,
          title: formData.signerTitle,
          companyName: formData.companyName,
          companyAddress: formData.companyAddress,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          businessStructure: formData.businessStructure
        },
        signerEmail: currentUser.email
      });

      const { envelopeId, signingUrl } = result.data;

      // Record the pending envelope on the user so the return page can find it
      await updateDoc(doc(db, 'users', currentUser.uid), {
        pendingNda: {
          envelopeId,
          startedAt: new Date().toISOString()
        },
        lastUpdated: new Date().toISOString()
      });

      // Redirect into the DocuSign embedded signing session
      window.location.href = signingUrl;
    } catch (error) {
      console.error('Error initiating DocuSign NDA signing:', error);
      setError(error.message || 'There was an error initiating the NDA signing. Please try again.');
      setIsSubmitting(false);
    }
  };


  const handleViewAuditTrail = async () => {
    try {
      // Create enhanced form data that includes dynamic form data
      const enhancedFormData = getEnhancedFormData();
      
      console.log('Audit trail enhanced form data:', enhancedFormData);
      
      // Create audit trail data with actual user input
      const sampleAuditData = {
        documentType: 'Non-Disclosure Agreement',
        documentId: `nda-${Date.now()}`,
        signatureData: {
          signerName: enhancedFormData.signerName || 'N/A',
          signerEmail: currentUser?.email || 'N/A',
          companyName: enhancedFormData.companyName || 'N/A',
          signature: enhancedFormData.signature || 'N/A'
        },
        eSignCompliance: {
          consentToElectronicSignatures: consentData.consentToElectronic,
          consentToElectronicRecords: consentData.consentToRecords,
          intentToSign: true
        },
        auditTrail: {
          signedDateTime: new Date().toISOString(),
          consentTimestamp: consentData.consentTimestamp,
          ipAddress: '192.168.1.1', // This would be real IP in production
          device: 'Desktop',
          browser: 'Chrome',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        },
        recordIntegrity: {
          tamperProofHash: 'abc123def456ghi789',
          documentSnapshot: {
            snapshotTimestamp: new Date().toISOString()
          }
        }
      };
      
      setAuditTrailData(sampleAuditData);
      setShowAuditTrail(true);
    } catch (error) {
      console.error('Error viewing audit trail:', error);
    }
  };

  // Extract variables from template content
  const extractVariablesFromContent = (content) => {
    const bracketPattern = /\[([A-Za-z_][A-Za-z0-9_]*)\]/g;
    const matches = content.match(bracketPattern);
    return matches ? matches.map(match => match.slice(1, -1)) : [];
  };

  // Get variable value from form data
  const getVariableValue = (variableName) => {
    const fieldName = variableName.toLowerCase().replace(/_/g, '');
    
    if (fieldName.includes('companyname') || fieldName.includes('company')) {
      return formData.companyName || `[${variableName}]`;
    } else if (fieldName.includes('state')) {
      return formData.state || `[${variableName}]`;
    } else if (fieldName.includes('address')) {
      return formData.companyAddress || `[${variableName}]`;
    } else if (fieldName.includes('city')) {
      return formData.city || `[${variableName}]`;
    } else if (fieldName.includes('zip') || fieldName.includes('zipcode')) {
      return formData.zipCode || `[${variableName}]`;
    } else if (fieldName.includes('businessstructure') || fieldName.includes('structure')) {
      return formData.businessStructure || `[${variableName}]`;
    } else if (fieldName.includes('signername') || fieldName.includes('signer')) {
      return formData.signerName || `[${variableName}]`;
    } else if (fieldName.includes('title')) {
      return formData.signerTitle || `[${variableName}]`;
    } else if (fieldName.includes('signature')) {
      return formData.signature || `[${variableName}]`;
    } else if (fieldName.includes('initial')) {
      return formData.initial || `[${variableName}]`;
    }
    
    return `[${variableName}]`;
  };

  // Get variable value from dynamic form data
  const getDynamicVariableValue = (variableId) => {
    return dynamicFormData[variableId] || '';
  };

  // Render content with variables replaced
  const renderContentWithVariables = (content, elementId = null) => {
    if (!content) return '';
    
    // First try to replace with dynamic form data
    let updatedContent = content;
    
    // Replace system variables first (these should be auto-populated, not client inputs)
    // Note: NDA doesn't typically use these system variables, but we'll handle them if they exist
    const systemVariables = {
      'Contract_Number': '[Contract Number]', // NDA doesn't have contract numbers
      'Program_Name': '[Program Name]', // NDA doesn't have program names
      'Program_Items': '[Program Items]', // NDA doesn't have program items
      'Monthly_Price': '[Monthly Price]', // NDA doesn't have monthly prices
      'Start_Date': new Date().toLocaleDateString()
    };
    
    // Replace system variables
    Object.entries(systemVariables).forEach(([variableName, value]) => {
      const systemPattern = new RegExp(`\\[${variableName}\\]`, 'g');
      updatedContent = updatedContent.replace(systemPattern, value);
    });
    
    // Replace special variables like [SIGNATURE], [INITIAL], [INITIALS], etc.
    // Use case-insensitive flag to match [Date], [DATE], [Signature], etc.
    const specialPattern = /\[(SIGNATURE|INITIAL|INITIALS|TEXT_INPUT|CHECKBOX|DATE)\]/gi;
    let dateMatchCount = 0; // Track which date match we're on (for highlighting only the first one)
    updatedContent = updatedContent.replace(specialPattern, (match, variableType) => {
      let variableTypeLower = variableType.toLowerCase();
      // Handle INITIALS -> initial mapping
      if (variableTypeLower === 'initials') {
        variableTypeLower = 'initial';
      }
      // Find the first variable of this type in client variables
      // Declare once at the top to avoid redeclaration errors
      const clientVariables = getClientVariables();
      
      // For date variables, always use the first one from variablesInOrder (maintains document order)
      // This ensures we always match the first date variable (the one at the top)
      let matchingVariable = null;
      if (variableTypeLower === 'date') {
        // For dates, always use the first one from variablesInOrder
        matchingVariable = variablesInOrder.find(v => v.type === 'date');
        if (!matchingVariable) {
          // Fallback to clientVariables if not in variablesInOrder
          matchingVariable = clientVariables.find(v => v.type === 'date');
        }
      } else {
        // For INITIAL/INITIALS, we need to match by originalContent to distinguish them
        const originalMatch = variableType.toUpperCase(); // This is the original match like "INITIAL" or "INITIALS"
        
        // Priority 1: If there's a highlighted variable, check if it matches this type and exact title
        // This ensures we match the exact variable that's currently highlighted
        // For INITIAL/INITIALS, match by exact title to avoid matching "Initials Two" when looking for "Initials"
        if (highlightedVariableId) {
          if (variableTypeLower === 'initial') {
            // For initial type, match by exact normalized title
            let expectedTitle = null;
            if (originalMatch === 'INITIALS') {
              expectedTitle = 'initials';
            } else if (originalMatch === 'INITIAL') {
              expectedTitle = 'initial';
            }
            
            if (expectedTitle) {
              const highlightedVar = variablesInOrder.find(v => 
                v.id === highlightedVariableId && 
                v.type === variableTypeLower &&
                v.variableTitle &&
                v.variableTitle.toLowerCase().trim() === expectedTitle
              );
              if (highlightedVar) {
                matchingVariable = highlightedVar;
              } else {
                // Also check if the highlighted variable is a bracket variable that matches
                const highlightedBracketVar = variablesInOrder.find(v => 
                  v.id === highlightedVariableId && 
                  v.isBracketVariable &&
                  v.bracketName &&
                  v.bracketName.toLowerCase() === expectedTitle.replace(/\s+/g, '_')
                );
                if (highlightedBracketVar) {
                  matchingVariable = highlightedBracketVar;
                }
              }
            }
          } else {
            // For other types, use original matching logic
            const highlightedVar = variablesInOrder.find(v => 
              v.id === highlightedVariableId && 
              v.type === variableTypeLower && 
              (v.originalContent ? v.originalContent.toUpperCase() === originalMatch : true)
            );
            if (highlightedVar) {
              matchingVariable = highlightedVar;
            }
          }
        }
        
        // Priority 2: Try to find one that matches the current element and exact title
        // Also check bracket variables for INITIAL/INITIALS
        if (!matchingVariable) {
          if (variableTypeLower === 'initial') {
            // For initial type, match by exact normalized title
            let expectedTitle = null;
            if (originalMatch === 'INITIALS') {
              expectedTitle = 'initials';
            } else if (originalMatch === 'INITIAL') {
              expectedTitle = 'initial';
            }
            
            if (expectedTitle) {
              matchingVariable = clientVariables.find(v => 
                v.type === variableTypeLower && 
                v.parentElementId === elementId &&
                v.variableTitle &&
                v.variableTitle.toLowerCase().trim() === expectedTitle
              );
              // Also check bracket variables
              if (!matchingVariable) {
                matchingVariable = clientVariables.find(v => 
                  v.isBracketVariable &&
                  v.bracketName &&
                  v.parentElementId === elementId &&
                  v.bracketName.toLowerCase() === expectedTitle.replace(/\s+/g, '_')
                );
              }
            }
          } else {
            // For other types, use original matching logic
            matchingVariable = clientVariables.find(v => 
              v.type === variableTypeLower && 
              v.parentElementId === elementId &&
              (v.originalContent ? v.originalContent.toUpperCase() === originalMatch : true)
            );
          }
        }
        
        // Priority 3: If no match found for current element, find the first variable of this type with matching originalContent
        // Also check bracket variables for INITIAL/INITIALS
        // IMPORTANT: For INITIAL/INITIALS, match by exact title to avoid matching "Initials Two" when looking for "Initials"
        if (!matchingVariable) {
          if (variableTypeLower === 'initial') {
            // For initial type, match by exact normalized title to distinguish "Initials", "Initials Two", "Initials Three"
            // Map the special pattern match to expected title
            let expectedTitle = null;
            if (originalMatch === 'INITIALS') {
              expectedTitle = 'initials'; // Match "Initials" exactly
            } else if (originalMatch === 'INITIAL') {
              expectedTitle = 'initial'; // Match "Initial" exactly
            }
            
            // Try to find in variablesInOrder first (maintains document order)
            if (expectedTitle) {
              const orderedMatch = variablesInOrder.find(v => 
                v.type === variableTypeLower &&
                v.variableTitle &&
                v.variableTitle.toLowerCase().trim() === expectedTitle
              );
              if (orderedMatch) {
                matchingVariable = orderedMatch;
              } else {
                // Also check bracket variables in variablesInOrder
                const orderedBracketMatch = variablesInOrder.find(v => 
                  v.isBracketVariable &&
                  v.bracketName &&
                  v.bracketName.toLowerCase() === expectedTitle.replace(/\s+/g, '_')
                );
                if (orderedBracketMatch) {
                  matchingVariable = orderedBracketMatch;
                }
              }
            }
            
            // Fallback to clientVariables
            if (!matchingVariable && expectedTitle) {
              matchingVariable = clientVariables.find(v => 
                v.type === variableTypeLower &&
                v.variableTitle &&
                v.variableTitle.toLowerCase().trim() === expectedTitle
              );
              if (!matchingVariable) {
                matchingVariable = clientVariables.find(v => 
                  v.isBracketVariable &&
                  v.bracketName &&
                  v.bracketName.toLowerCase() === expectedTitle.replace(/\s+/g, '_')
                );
              }
            }
            
            // If still no match, fall back to originalContent matching (for backward compatibility)
            if (!matchingVariable) {
              const orderedMatch = variablesInOrder.find(v => 
                v.type === variableTypeLower &&
                (v.originalContent ? v.originalContent.toUpperCase() === originalMatch : true)
              );
              if (orderedMatch) {
                matchingVariable = orderedMatch;
              } else {
                matchingVariable = clientVariables.find(v => 
                  v.type === variableTypeLower &&
                  (v.originalContent ? v.originalContent.toUpperCase() === originalMatch : true)
                );
              }
            }
          } else {
            // For other types, use original matching logic
            const orderedMatch = variablesInOrder.find(v => 
              v.type === variableTypeLower &&
              (v.originalContent ? v.originalContent.toUpperCase() === originalMatch : true)
            );
            if (orderedMatch) {
              matchingVariable = orderedMatch;
            } else {
              matchingVariable = clientVariables.find(v => 
                v.type === variableTypeLower &&
                (v.originalContent ? v.originalContent.toUpperCase() === originalMatch : true)
              );
            }
          }
        }
        
        // Priority 4: If still no match (for backward compatibility), try without originalContent check
        // But for initial type, still use exact title matching to avoid matching wrong variable
        if (!matchingVariable) {
          if (variableTypeLower === 'initial') {
            // For initial type, still match by exact title even in fallback
            let expectedTitle = null;
            if (originalMatch === 'INITIALS') {
              expectedTitle = 'initials';
            } else if (originalMatch === 'INITIAL') {
              expectedTitle = 'initial';
            }
            
            if (expectedTitle && highlightedVariableId) {
              const highlightedVar = variablesInOrder.find(v => 
                v.id === highlightedVariableId && 
                v.type === variableTypeLower &&
                v.variableTitle &&
                v.variableTitle.toLowerCase().trim() === expectedTitle
              );
              if (highlightedVar) {
                matchingVariable = highlightedVar;
              }
            }
            // Don't fall back to matching by type only for initial type - we want exact matches
          } else {
            // For other types, use original fallback logic
            if (highlightedVariableId) {
              const highlightedVar = variablesInOrder.find(v => 
                v.id === highlightedVariableId && 
                v.type === variableTypeLower
              );
              if (highlightedVar) {
                matchingVariable = highlightedVar;
              }
            }
            if (!matchingVariable) {
              matchingVariable = clientVariables.find(v => 
                v.type === variableTypeLower && 
                v.parentElementId === elementId
              );
              if (!matchingVariable) {
                const orderedMatch = variablesInOrder.find(v => v.type === variableTypeLower);
                if (orderedMatch) {
                  matchingVariable = orderedMatch;
                } else {
                  matchingVariable = clientVariables.find(v => v.type === variableTypeLower);
                }
              }
            }
          }
        }
      }
      
      
      // Check if this is a BBA variable (should not be replaced)
      if (!matchingVariable) {
        const allVariables = getAllVariables();
        const bbaVariable = allVariables.find(v => 
          v.type === variableTypeLower && 
          v.assignedTo === 'BBA' &&
          v.parentElementId === elementId
        );
        if (bbaVariable) {
          return match; // Keep original placeholder for BBA variables
        }
      }
      
      
      if (matchingVariable) {
        // Check if the matching variable has a value, or if any instance of this variable has a value
        let value = null;
        if (matchingVariable.assignedTo === 'Client') {
          // First check if the matched variable has a value
          if (dynamicFormData[matchingVariable.id]) {
            value = dynamicFormData[matchingVariable.id];
          } else {
            // If not, check if any other instance of this variable has a value
            // For INITIAL/INITIALS, match by exact title to avoid getting value from "Initials Two" when looking for "Initials"
            // Use the originalMatch from the outer scope (defined earlier in the else block)
            const originalMatchForValue = variableTypeLower === 'date' ? null : variableType.toUpperCase();
            let variableWithValue = null;
            
            if (variableTypeLower === 'initial' && originalMatchForValue) {
              // For initial type, match by exact normalized title
              let expectedTitle = null;
              if (originalMatchForValue === 'INITIALS') {
                expectedTitle = 'initials';
              } else if (originalMatchForValue === 'INITIAL') {
                expectedTitle = 'initial';
              }
              
              if (expectedTitle) {
                // First check special variables with exact title match
                variableWithValue = clientVariables.find(v => 
                  v.type === variableTypeLower && 
                  v.variableTitle &&
                  v.variableTitle.toLowerCase().trim() === expectedTitle &&
                  dynamicFormData[v.id]
                ) || variablesInOrder.find(v => 
                  v.type === variableTypeLower && 
                  v.variableTitle &&
                  v.variableTitle.toLowerCase().trim() === expectedTitle &&
                  dynamicFormData[v.id]
                );
                
                // Also check bracket variables with exact bracket name match
                if (!variableWithValue) {
                  variableWithValue = clientVariables.find(v => 
                    v.isBracketVariable &&
                    v.bracketName &&
                    v.bracketName.toLowerCase() === expectedTitle.replace(/\s+/g, '_') &&
                    dynamicFormData[v.id]
                  ) || variablesInOrder.find(v => 
                    v.isBracketVariable &&
                    v.bracketName &&
                    v.bracketName.toLowerCase() === expectedTitle.replace(/\s+/g, '_') &&
                    dynamicFormData[v.id]
                  );
                }
              }
            } else {
              // For other types, use original matching logic
              variableWithValue = clientVariables.find(v => 
                v.type === variableTypeLower && 
                dynamicFormData[v.id] &&
                (originalMatchForValue && v.originalContent ? v.originalContent.toUpperCase() === originalMatchForValue : true)
              ) || variablesInOrder.find(v => 
                v.type === variableTypeLower && 
                dynamicFormData[v.id] &&
                (originalMatchForValue && v.originalContent ? v.originalContent.toUpperCase() === originalMatchForValue : true)
              );
            }
            if (variableWithValue) {
              value = dynamicFormData[variableWithValue.id];
              // Keep the original matchingVariable for highlighting, but use the value from variableWithValue
              // Don't update matchingVariable here - we want to highlight the correct variable, not necessarily the one with the value
            }
          }
        }
        
        const isHighlighted = highlightedVariableId === matchingVariable.id;
        const highlightClass = isHighlighted 
          ? 'transition-all duration-300 bg-yellow-200 dark:bg-yellow-900/70 rounded px-1 border-2 border-yellow-400 dark:border-yellow-600' 
          : '';
        // Always include data-variable-id so the variable can be found for scrolling/highlighting
        const highlightAttr = matchingVariable ? `data-variable-id="${matchingVariable.id}"` : '';
        
        // Only use dynamic form data for Client-assigned variables
        if (matchingVariable.assignedTo === 'Client' && value) {
          let formattedValue = value;
          
          // Format date value for display (convert YYYY-MM-DD to readable format)
          if (variableTypeLower === 'date' && formattedValue) {
            try {
              // Parse YYYY-MM-DD format manually to avoid timezone issues
              // When using new Date() with YYYY-MM-DD, it's interpreted as UTC midnight
              // which can shift the date back by a day in some timezones
              if (typeof formattedValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(formattedValue)) {
                const [year, month, day] = formattedValue.split('-').map(Number);
                const dateObj = new Date(year, month - 1, day); // month is 0-indexed
                // Format as "Month Day, Year" (e.g., "November 13, 2025")
                formattedValue = dateObj.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                });
              } else {
                // Fallback for other date formats
                const dateObj = new Date(formattedValue);
                if (!isNaN(dateObj.getTime())) {
                  formattedValue = dateObj.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  });
                }
              }
            } catch (e) {
              // If date parsing fails, use the value as-is
            }
          }
          
          // For date variables, handle highlighting for the first occurrence
          if (variableTypeLower === 'date') {
            const firstDateVariable = variablesInOrder.find(v => v.type === 'date');
            // Check if this is the first date variable (which it should be since we always use the first one)
            if (firstDateVariable && matchingVariable && matchingVariable.id === firstDateVariable.id) {
              // This is the first date variable - always inject and highlight if it's the current variable
              // Only increment dateMatchCount on first match to prevent highlighting subsequent [DATE] placeholders
              if (dateMatchCount === 0) {
                dateMatchCount++;
              }
              return `<span ${highlightAttr} class="${highlightClass}">${formattedValue}</span>`;
            } else if (variableTypeLower === 'date') {
              // For subsequent date variables, just return the value without highlighting
              if (dateMatchCount === 0) {
                dateMatchCount++;
              }
              return formattedValue;
            }
          }
          // Apply cursive styling for signature text - only for Client-assigned variables
          if (variableTypeLower === 'signature') {
            return `<span ${highlightAttr} class="${highlightClass}" style="font-family: 'Dancing Script', 'Brush Script MT', cursive; font-size: 1.2em; font-weight: 600;">${formattedValue}</span>`;
          }
          return `<span ${highlightAttr} class="${highlightClass}">${formattedValue}</span>`;
        }
        // For BBA-assigned variables, keep the original placeholder but still allow highlighting
        if (matchingVariable.assignedTo === 'BBA') {
          if (isHighlighted) {
            return `<span ${highlightAttr} class="${highlightClass}">${match}</span>`;
          }
          return match; // Keep original placeholder for BBA variables
        }
        
        // If we have a matching variable but no value, show placeholder with highlighting if needed
        if (!value && matchingVariable.assignedTo === 'Client') {
          const userFriendlyPlaceholder = getUserFriendlyPlaceholder(match);
          if (isHighlighted) {
            return `<span ${highlightAttr} class="${highlightClass}">${userFriendlyPlaceholder}</span>`;
          }
          return userFriendlyPlaceholder;
        }
      }
      
      // Fall back to legacy formData for initials
      if (variableTypeLower === 'initial' && formData.initial) {
        // Try to find matching variable for highlighting
        const matchingInitial = clientVariables.find(v => 
          v.type === 'initial' && v.parentElementId === elementId
        );
        const isHighlighted = matchingInitial && highlightedVariableId === matchingInitial.id;
        const highlightClass = isHighlighted 
          ? 'transition-all duration-300 bg-yellow-200 dark:bg-yellow-900/70 rounded px-1 border-2 border-yellow-400 dark:border-yellow-600' 
          : '';
        const highlightAttr = isHighlighted ? `data-variable-id="${matchingInitial.id}"` : '';
        return `<span ${highlightAttr} class="${highlightClass}">${formData.initial}</span>`;
      }
      
      // Fall back to legacy formData for signature - only apply cursive if it's a Client variable
      if (variableTypeLower === 'signature' && formData.signature) {
        // Check if this is a Client-assigned variable by looking at the matching variable
        const matchingSignature = clientVariables.find(v => 
          v.type === 'signature' && v.assignedTo === 'Client' && v.parentElementId === elementId
        );
        const isHighlighted = matchingSignature && highlightedVariableId === matchingSignature.id;
        const highlightClass = isHighlighted 
          ? 'transition-all duration-300 bg-yellow-200 dark:bg-yellow-900/70 rounded px-1 border-2 border-yellow-400 dark:border-yellow-600' 
          : '';
        const highlightAttr = isHighlighted ? `data-variable-id="${matchingSignature.id}"` : '';
        if (matchingSignature) {
          return `<span ${highlightAttr} class="${highlightClass}" style="font-family: 'Dancing Script', 'Brush Script MT', cursive; font-size: 1.2em; font-weight: 600;">${formData.signature}</span>`;
        }
        return `<span ${highlightAttr} class="${highlightClass}">${formData.signature}</span>`;
      }
      
      // Fall back to legacy formData for date - format it properly
      if (variableTypeLower === 'date') {
        // Try to find matching variable for highlighting
        const matchingDate = clientVariables.find(v => 
          v.type === 'date' && v.parentElementId === elementId
        ) || variablesInOrder.find(v => v.type === 'date');
        
        if (matchingDate && dynamicFormData[matchingDate.id]) {
          let dateValue = dynamicFormData[matchingDate.id];
          // Format date value for display (avoid timezone issues)
          try {
            // Parse YYYY-MM-DD format manually to avoid timezone issues
            if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
              const [year, month, day] = dateValue.split('-').map(Number);
              const dateObj = new Date(year, month - 1, day); // month is 0-indexed
              dateValue = dateObj.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              });
            } else {
              // Fallback for other date formats
              const dateObj = new Date(dateValue);
              if (!isNaN(dateObj.getTime())) {
                dateValue = dateObj.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                });
              }
            }
          } catch (e) {
            // If date parsing fails, use the value as-is
          }
          
          const isHighlighted = highlightedVariableId === matchingDate.id;
          const highlightClass = isHighlighted 
            ? 'transition-all duration-300 bg-yellow-200 dark:bg-yellow-900/70 rounded px-1 border-2 border-yellow-400 dark:border-yellow-600' 
            : '';
          const highlightAttr = isHighlighted ? `data-variable-id="${matchingDate.id}"` : '';
          
          // For date variables, only highlight the first occurrence
          if (matchingDate.id === variablesInOrder.find(v => v.type === 'date')?.id) {
            if (dateMatchCount === 0) {
              dateMatchCount++;
              return `<span ${highlightAttr} class="${highlightClass}">${dateValue}</span>`;
            }
          }
          dateMatchCount++;
          return dateValue;
        }
      }
      
      // If no value found, check if we should highlight the placeholder
      // For date variables, always use the first one from variablesInOrder
      let matchingPlaceholder = null;
      if (variableTypeLower === 'date') {
        // For dates, always use the first one from variablesInOrder
        matchingPlaceholder = variablesInOrder.find(v => v.type === 'date');
        if (!matchingPlaceholder) {
          // Fallback to clientVariables if not in variablesInOrder
          matchingPlaceholder = clientVariables.find(v => v.type === 'date');
        }
      } else {
        // For INITIAL/INITIALS, we need to match by exact title to distinguish "Initials", "Initials Two", "Initials Three"
        const originalMatch = variableType.toUpperCase(); // This is the original match like "INITIAL" or "INITIALS"
        
        if (variableTypeLower === 'initial') {
          // For initial type, match by exact normalized title
          let expectedTitle = null;
          if (originalMatch === 'INITIALS') {
            expectedTitle = 'initials';
          } else if (originalMatch === 'INITIAL') {
            expectedTitle = 'initial';
          }
          
          // Priority 1: If there's a highlighted variable, check if it matches this type and exact title
          if (highlightedVariableId && expectedTitle) {
            const highlightedVar = variablesInOrder.find(v => 
              v.id === highlightedVariableId && 
              v.type === variableTypeLower &&
              v.variableTitle &&
              v.variableTitle.toLowerCase().trim() === expectedTitle
            );
            if (highlightedVar) {
              matchingPlaceholder = highlightedVar;
            } else {
              // Also check bracket variables
              const highlightedBracketVar = variablesInOrder.find(v => 
                v.id === highlightedVariableId && 
                v.isBracketVariable &&
                v.bracketName &&
                v.bracketName.toLowerCase() === expectedTitle.replace(/\s+/g, '_')
              );
              if (highlightedBracketVar) {
                matchingPlaceholder = highlightedBracketVar;
              }
            }
          }
          
          // Priority 2: Try to find one that matches the current element and exact title
          if (!matchingPlaceholder && expectedTitle) {
            matchingPlaceholder = clientVariables.find(v => 
              v.type === variableTypeLower && 
              v.parentElementId === elementId &&
              v.variableTitle &&
              v.variableTitle.toLowerCase().trim() === expectedTitle
            );
            if (!matchingPlaceholder) {
              matchingPlaceholder = clientVariables.find(v => 
                v.isBracketVariable &&
                v.bracketName &&
                v.parentElementId === elementId &&
                v.bracketName.toLowerCase() === expectedTitle.replace(/\s+/g, '_')
              );
            }
          }
          
          // Priority 3: If no match for current element, find the first variable of this type with exact title
          if (!matchingPlaceholder && expectedTitle) {
            const orderedMatch = variablesInOrder.find(v => 
              v.type === variableTypeLower &&
              v.variableTitle &&
              v.variableTitle.toLowerCase().trim() === expectedTitle
            );
            if (orderedMatch) {
              matchingPlaceholder = orderedMatch;
            } else {
              matchingPlaceholder = clientVariables.find(v => 
                v.type === variableTypeLower &&
                v.variableTitle &&
                v.variableTitle.toLowerCase().trim() === expectedTitle
              );
              if (!matchingPlaceholder) {
                // Also check bracket variables
                matchingPlaceholder = variablesInOrder.find(v => 
                  v.isBracketVariable &&
                  v.bracketName &&
                  v.bracketName.toLowerCase() === expectedTitle.replace(/\s+/g, '_')
                ) || clientVariables.find(v => 
                  v.isBracketVariable &&
                  v.bracketName &&
                  v.bracketName.toLowerCase() === expectedTitle.replace(/\s+/g, '_')
                );
              }
            }
          }
        } else {
          // For other types, use original matching logic
          // Priority 1: If there's a highlighted variable, check if it matches this type and originalContent first
          if (highlightedVariableId) {
            const highlightedVar = variablesInOrder.find(v => 
              v.id === highlightedVariableId && 
              v.type === variableTypeLower &&
              (v.originalContent ? v.originalContent.toUpperCase() === originalMatch : true)
            );
            if (highlightedVar) {
              matchingPlaceholder = highlightedVar;
            }
          }
          
          // Priority 2: Try to find one that matches the current element and originalContent
          if (!matchingPlaceholder) {
            matchingPlaceholder = clientVariables.find(v => 
              v.type === variableTypeLower && 
              v.parentElementId === elementId &&
              (v.originalContent ? v.originalContent.toUpperCase() === originalMatch : true)
            );
          }
          
          // Priority 3: If no match for current element, find the first variable of this type with matching originalContent
          if (!matchingPlaceholder) {
            const orderedMatch = variablesInOrder.find(v => 
              v.type === variableTypeLower &&
              (v.originalContent ? v.originalContent.toUpperCase() === originalMatch : true)
            );
            if (orderedMatch) {
              matchingPlaceholder = orderedMatch;
            } else {
              matchingPlaceholder = clientVariables.find(v => 
                v.type === variableTypeLower &&
                (v.originalContent ? v.originalContent.toUpperCase() === originalMatch : true)
              );
            }
          }
        }
        
        // Priority 4: If still no match (for backward compatibility), try without exact matching
        // But for initial type, still use exact title matching to avoid matching wrong variable
        if (!matchingPlaceholder) {
          if (variableTypeLower === 'initial') {
            // For initial type, still match by exact title even in fallback
            let expectedTitle = null;
            if (originalMatch === 'INITIALS') {
              expectedTitle = 'initials';
            } else if (originalMatch === 'INITIAL') {
              expectedTitle = 'initial';
            }
            
            if (expectedTitle && highlightedVariableId) {
              const highlightedVar = variablesInOrder.find(v => 
                v.id === highlightedVariableId && 
                v.type === variableTypeLower &&
                v.variableTitle &&
                v.variableTitle.toLowerCase().trim() === expectedTitle
              );
              if (highlightedVar) {
                matchingPlaceholder = highlightedVar;
              }
            }
          } else {
            // For other types, use original fallback logic
            if (highlightedVariableId) {
              const highlightedVar = variablesInOrder.find(v => 
                v.id === highlightedVariableId && 
                v.type === variableTypeLower
              );
              if (highlightedVar) {
                matchingPlaceholder = highlightedVar;
              }
            }
            if (!matchingPlaceholder) {
              matchingPlaceholder = clientVariables.find(v => 
                v.type === variableTypeLower && v.parentElementId === elementId
              );
              if (!matchingPlaceholder) {
                const orderedMatch = variablesInOrder.find(v => v.type === variableTypeLower);
                if (orderedMatch) {
                  matchingPlaceholder = orderedMatch;
                } else {
                  matchingPlaceholder = clientVariables.find(v => v.type === variableTypeLower);
                }
              }
            }
          }
        }
      }
      
      // Only highlight if this matches the currently highlighted variable
      // For date variables, we want to highlight only the first one (top one) when it's the current variable
      const isHighlighted = matchingPlaceholder && highlightedVariableId === matchingPlaceholder.id;
      
      // Convert bracket notation to user-friendly placeholder
      const userFriendlyPlaceholder = getUserFriendlyPlaceholder(match);
      
      // For date variables, only highlight the first occurrence (dateMatchCount === 0)
      if (variableTypeLower === 'date') {
        const firstDateVariable = variablesInOrder.find(v => v.type === 'date');
        // Only highlight if this is the first date variable (top one) AND it's the first match
        if (firstDateVariable && matchingPlaceholder && matchingPlaceholder.id === firstDateVariable.id) {
          if (dateMatchCount === 0) {
            dateMatchCount++;
            if (isHighlighted) {
              const highlightClass = 'transition-all duration-300 bg-yellow-200 dark:bg-yellow-900/70 rounded px-1 border-2 border-yellow-400 dark:border-yellow-600';
              return `<span data-variable-id="${matchingPlaceholder.id}" class="${highlightClass}">${userFriendlyPlaceholder}</span>`;
            }
            // Return placeholder even if not highlighted (for first match)
            return userFriendlyPlaceholder;
          }
          // Already processed first match, return placeholder without highlight
          return userFriendlyPlaceholder;
        } else if (variableTypeLower === 'date') {
          if (dateMatchCount === 0) {
            dateMatchCount++;
          }
          // Return placeholder for subsequent date variables
          return userFriendlyPlaceholder;
        }
      }
      
      if (isHighlighted) {
        const highlightClass = 'transition-all duration-300 bg-yellow-200 dark:bg-yellow-900/70 rounded px-1 border-2 border-yellow-400 dark:border-yellow-600';
        return `<span data-variable-id="${matchingPlaceholder.id}" class="${highlightClass}">${userFriendlyPlaceholder}</span>`;
      }
      
      // Return user-friendly placeholder even if not highlighted
      return userFriendlyPlaceholder;
    });
    
    // Replace bracket variables like [Company_Name] - but skip system variables and BBA variables as they're already handled
    const bracketPattern = /\[([A-Za-z_][A-Za-z0-9_]*)\]/g;
    updatedContent = updatedContent.replace(bracketPattern, (match, variableName) => {
      // Skip system variables as they're already handled above
      if (['Contract_Number', 'Program_Name', 'Program_Items', 'Monthly_Price', 'Start_Date'].includes(variableName)) {
        return match; // This should have been replaced above, but just in case
      }
      
      // Skip BBA variables (they start with BBA_ prefix and should not be replaced by client input)
      if (variableName.startsWith('BBA_')) {
        return match; // Keep original placeholder for BBA variables
      }
      
      
      // First try to find in client variables
      const clientVariables = getClientVariables();
      
      // Priority 1: If there's a highlighted variable, check if it matches this bracket name first
      // This ensures we match the exact variable that's currently highlighted
      let matchingVariable = null;
      if (highlightedVariableId) {
        const highlightedVar = variablesInOrder.find(v => 
          v.id === highlightedVariableId && 
          v.isBracketVariable && 
          v.bracketName === variableName
        );
        if (highlightedVar) {
          matchingVariable = highlightedVar;
        }
      }
      
      // Priority 2: Try to find by parentElementId (most accurate match for this specific instance)
      if (!matchingVariable) {
        matchingVariable = clientVariables.find(v => 
        v.isBracketVariable && 
        v.bracketName === variableName && 
        v.parentElementId === elementId
      );
      }
      
      // Priority 3: If not found by parentElementId, try to find any variable with this bracket name
      // This handles cases where parentElementId might not match exactly
      if (!matchingVariable) {
        // Try variablesInOrder (maintains document order)
        matchingVariable = variablesInOrder.find(v => 
          v.isBracketVariable && 
          v.bracketName === variableName
        );
        
        // Last resort: try clientVariables without parentElementId constraint
        if (!matchingVariable) {
          matchingVariable = clientVariables.find(v => 
            v.isBracketVariable && 
            v.bracketName === variableName
          );
        }
      }
      
      // Debug logging for Business Structure
      if (variableName === 'Business_Structure') {
        const bsInOrder = variablesInOrder.filter(v => v.bracketName === 'Business_Structure');
        const highlightedVar = highlightedVariableId ? variablesInOrder.find(v => v.id === highlightedVariableId) : null;
        console.log('Business Structure matching:', {
          variableName,
          elementId,
          matchingVariable: matchingVariable ? { id: matchingVariable.id, bracketName: matchingVariable.bracketName, parentElementId: matchingVariable.parentElementId } : null,
          highlightedVariableId,
          highlightedVarInOrder: highlightedVar ? { id: highlightedVar.id, bracketName: highlightedVar.bracketName } : null,
          hasValue: matchingVariable ? !!dynamicFormData[matchingVariable.id] : false,
          value: matchingVariable ? dynamicFormData[matchingVariable.id] : null,
          variablesInOrderBS: bsInOrder.map(v => ({ id: v.id, bracketName: v.bracketName, parentElementId: v.parentElementId }))
        });
      }
      
      // Check if the matching variable has a value, or if any instance of this variable has a value
      let value = null;
      let valueVariableId = null;
      if (matchingVariable) {
        // First check if the matched variable has a value
        if (dynamicFormData[matchingVariable.id]) {
          value = dynamicFormData[matchingVariable.id];
          valueVariableId = matchingVariable.id;
        } else {
          // If not, check if any other instance of this variable has a value
          // This ensures values persist even when matching a different instance
          const variableWithValue = clientVariables.find(v => 
            v.isBracketVariable && 
            v.bracketName === variableName && 
            dynamicFormData[v.id]
          ) || variablesInOrder.find(v => 
            v.isBracketVariable && 
            v.bracketName === variableName && 
            dynamicFormData[v.id]
          );
          if (variableWithValue) {
            value = dynamicFormData[variableWithValue.id];
            valueVariableId = variableWithValue.id;
            // Use the variable with the value for highlighting purposes
            matchingVariable = variableWithValue;
          }
        }
      }
      
      // Check if highlighted after potentially updating matchingVariable
      const isHighlighted = matchingVariable && highlightedVariableId === matchingVariable.id;
      const highlightClass = isHighlighted 
        ? 'transition-all duration-300 bg-yellow-200 dark:bg-yellow-900/70 rounded px-1 border-2 border-yellow-400 dark:border-yellow-600' 
        : '';
      // Always include data-variable-id so the variable can be found for scrolling/highlighting
      const highlightAttr = matchingVariable ? `data-variable-id="${matchingVariable.id}"` : '';
      
      if (matchingVariable && value) {
        // Apply cursive styling for signature text - only for Client-assigned variables
        if (variableName.toLowerCase().includes('signature') || variableName.toLowerCase().includes('sign')) {
          return `<span ${highlightAttr} class="${highlightClass}" style="font-family: 'Dancing Script', 'Brush Script MT', cursive; font-size: 1.2em; font-weight: 600;">${value}</span>`;
        }
        // Always include highlight attributes even when there's a value (for highlighting)
        return `<span ${highlightAttr} class="${highlightClass}">${value}</span>`;
      }
      
      // If no value but variable is found and should be highlighted, highlight the placeholder
      if (matchingVariable && isHighlighted) {
        const userFriendlyPlaceholder = getUserFriendlyPlaceholder(match);
        return `<span ${highlightAttr} class="${highlightClass}">${userFriendlyPlaceholder}</span>`;
      }
      
      // If no value but variable is found, return user-friendly placeholder
      if (matchingVariable) {
        return getUserFriendlyPlaceholder(match);
      }
      
      // Check for original template variables (these are always Client-assigned)
      const originalVariable = clientVariables.find(v => 
        v.isOriginalTemplate && 
        v.originalField && 
        v.originalField.variable === match
      );
      if (originalVariable) {
        const isOriginalHighlighted = highlightedVariableId === originalVariable.id;
        const originalHighlightClass = isOriginalHighlighted 
          ? 'transition-all duration-300 bg-yellow-200 dark:bg-yellow-900/70 rounded px-1 border-2 border-yellow-400 dark:border-yellow-600' 
          : '';
        const originalHighlightAttr = isOriginalHighlighted ? `data-variable-id="${originalVariable.id}"` : '';
        
        if (dynamicFormData[originalVariable.id]) {
          return `<span ${originalHighlightAttr} class="${originalHighlightClass}">${dynamicFormData[originalVariable.id]}</span>`;
        }
        // If no value, highlight the placeholder if needed
        if (isOriginalHighlighted) {
          const userFriendlyPlaceholder = getUserFriendlyPlaceholder(match);
          return `<span ${originalHighlightAttr} class="${originalHighlightClass}">${userFriendlyPlaceholder}</span>`;
        }
        // Return user-friendly placeholder even if not highlighted
        return getUserFriendlyPlaceholder(match);
      }
      
      // Check if this is a BBA variable (should not be replaced)
      const allVariables = getAllVariables();
      const bbaVariable = allVariables.find(v => 
        v.isBracketVariable && 
        v.bracketName === variableName && 
        v.assignedTo === 'BBA' &&
        v.parentElementId === elementId
      );
      if (bbaVariable) {
        // Still allow highlighting for BBA variables, but show user-friendly placeholder
        const isBbaHighlighted = highlightedVariableId === bbaVariable.id;
        const userFriendlyPlaceholder = getUserFriendlyPlaceholder(match);
        if (isBbaHighlighted) {
          const bbaHighlightClass = 'transition-all duration-300 bg-yellow-200 dark:bg-yellow-900/70 rounded px-1 border-2 border-yellow-400 dark:border-yellow-600';
          return `<span data-variable-id="${bbaVariable.id}" class="${bbaHighlightClass}">${userFriendlyPlaceholder}</span>`;
        }
        return userFriendlyPlaceholder; // Show user-friendly placeholder for BBA variables
      }
      
      // Fall back to legacy form data for Client variables only
      // Check if this is a Client variable (either by parentElementId or in variablesInOrder)
      const isClientVariable = clientVariables.some(v => 
        v.isBracketVariable && 
        v.bracketName === variableName && 
        v.parentElementId === elementId
      ) || variablesInOrder.some(v => 
        v.isBracketVariable && 
        v.bracketName === variableName
      );
      
      if (isClientVariable) {
        const legacyValue = getVariableValue(variableName);
        if (legacyValue !== `[${variableName}]`) {
          // Apply cursive styling for signature text - only for Client-assigned variables
          if (variableName.toLowerCase().includes('signature') || variableName.toLowerCase().includes('sign')) {
            return `<span ${highlightAttr} class="${highlightClass}" style="font-family: 'Dancing Script', 'Brush Script MT', cursive; font-size: 1.2em; font-weight: 600;">${legacyValue}</span>`;
          }
          return `<span ${highlightAttr} class="${highlightClass}">${legacyValue}</span>`;
        }
        // If no value but should be highlighted, highlight the placeholder
        if (isHighlighted) {
          const userFriendlyPlaceholder = getUserFriendlyPlaceholder(match);
          return `<span ${highlightAttr} class="${highlightClass}">${userFriendlyPlaceholder}</span>`;
        }
        // Return user-friendly placeholder even if not highlighted
        return getUserFriendlyPlaceholder(match);
      }
      
      // For BBA-assigned variables or unassigned variables, show user-friendly placeholder
      return getUserFriendlyPlaceholder(match);
    });
    
    return updatedContent;
  };

  // Group client variables by their normalized names to detect duplicates
  const getGroupedClientVariables = () => {
    const clientVariables = getClientVariables();
    const grouped = {};
    
    clientVariables.forEach(variable => {
      // Create a normalized key for grouping (case-insensitive, trimmed) + assignedTo to prevent mixing Client/BBA
      const normalizedKey = `${variable.variableTitle.toLowerCase().trim()}-${variable.assignedTo}`;
      
      if (!grouped[normalizedKey]) {
        grouped[normalizedKey] = {
          key: normalizedKey,
          variables: [],
          primaryVariable: variable
        };
      }
      
      grouped[normalizedKey].variables.push(variable);
    });
    
    const result = Object.values(grouped);
    return result;
  };

  // Handle next variable
  const handleNext = () => {
    if (currentInputIndex < variablesInOrder.length - 1) {
      setCurrentInputIndex(currentInputIndex + 1);
    }
  };

  // Handle previous variable
  const handlePrevious = () => {
    if (currentInputIndex > 0) {
      setCurrentInputIndex(currentInputIndex - 1);
    }
  };

  // Render current input field (single field at a time)
  const renderCurrentInputField = () => {
    if (variablesInOrder.length === 0 || currentInputIndex >= variablesInOrder.length) {
      return null;
    }

    const currentVariable = variablesInOrder[currentInputIndex];
    const fieldId = `client-field-${currentVariable.id}`;
    
    // Get current value based on variable type
    let currentValue = '';
    if (currentVariable.isConsentCheckbox && currentVariable.consentType) {
      currentValue = consentData[currentVariable.consentType] ? 'true' : 'false';
    } else if (currentVariable.isAgreementCheckbox) {
      currentValue = hasAgreed ? 'true' : 'false';
    } else {
      currentValue = dynamicFormData[currentVariable.id] || '';
    }
    
    const handleChange = (value) => {
      // Handle consent checkboxes
      if (currentVariable.isConsentCheckbox && currentVariable.consentType) {
        setConsentData(prev => ({
          ...prev,
          [currentVariable.consentType]: value === 'true' || value === true,
          consentTimestamp: new Date().toISOString()
        }));
        setDynamicFormData(prev => ({ ...prev, [currentVariable.id]: value }));
        return;
      }
      
      // Handle agreement checkbox
      if (currentVariable.isAgreementCheckbox) {
        setHasAgreed(value === 'true' || value === true);
        setDynamicFormData(prev => ({ ...prev, [currentVariable.id]: value }));
        return;
      }
      
      // Update all variables in the group if this is part of a group
      const groupedVariables = getGroupedClientVariables();
      const group = groupedVariables.find(g => 
        g.variables.some(v => v.id === currentVariable.id)
      );
      
      const updates = {};
      if (group) {
        group.variables.forEach(variable => {
          updates[variable.id] = value;
        });
      } else {
        updates[currentVariable.id] = value;
      }
      
      setDynamicFormData(prev => ({ ...prev, ...updates }));
      
      // Also update legacy formData
      const title = currentVariable.variableTitle.toLowerCase();
      if (currentVariable.type === 'initial') {
        setFormData(prev => ({ ...prev, initial: value }));
      } else if (currentVariable.type === 'signature') {
        setFormData(prev => ({ ...prev, signature: value }));
      } else if (title.includes('signer') && title.includes('name')) {
        setFormData(prev => ({ ...prev, signerName: value }));
      } else if (title === 'title' || (title.includes('signer') && title.includes('title')) || title.includes('position')) {
        setFormData(prev => ({ ...prev, signerTitle: value }));
      } else if (title.includes('company') && title.includes('name')) {
        setFormData(prev => ({ ...prev, companyName: value }));
      } else if (title === 'address' || title.includes('address')) {
        setFormData(prev => ({ ...prev, companyAddress: value }));
      } else if (title.includes('city')) {
        setFormData(prev => ({ ...prev, city: value }));
      } else if (title.includes('state')) {
        setFormData(prev => ({ ...prev, state: value }));
      } else if (title.includes('zip')) {
        setFormData(prev => ({ ...prev, zipCode: value }));
      } else if (title.includes('business') && title.includes('structure')) {
        setFormData(prev => ({ ...prev, businessStructure: value }));
      } else if (title.includes('name') && !title.includes('company')) {
        // Fallback for name fields
        setFormData(prev => ({ ...prev, signerName: value }));
      } else if (title.includes('title') || title.includes('position')) {
        // Fallback for title fields
        setFormData(prev => ({ ...prev, signerTitle: value }));
      }
    };

    // Check if current input has a value
    const hasValue = () => {
      if (currentVariable.isConsentCheckbox || currentVariable.isAgreementCheckbox) {
        if (currentVariable.isConsentCheckbox && currentVariable.consentType) {
          return consentData[currentVariable.consentType];
        }
        if (currentVariable.isAgreementCheckbox) {
          return hasAgreed;
        }
      }
      return currentValue && (typeof currentValue !== 'string' || currentValue.trim());
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && hasValue()) {
        e.preventDefault();
        handleNext();
      }
    };

    // DocuSign confirmation step — replaces the old E-SIGN consent checkboxes
    if (currentVariable.isCombinedConsentCheckbox) {
      return (
        <div className="mb-6 p-4 border rounded-lg bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600">
          <div className="mb-4">
            <div className="flex items-center mb-3">
              <Shield className={`h-5 w-5 mr-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              <h3 className={`text-sm font-medium ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                Ready to Sign with DocuSign
              </h3>
            </div>
          </div>

          <div className="mb-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              ({currentInputIndex + 1} of {variablesInOrder.length})
            </span>
          </div>

          <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            You will be redirected to DocuSign to securely sign your NDA. After you sign,
            our team will counter-sign and you will receive a fully executed copy by email.
          </p>

          {/* Agreement confirmation */}
          <div className="flex items-start mb-4">
            <div className="flex items-center h-5">
              <input
                id={`${fieldId}-agreement`}
                type="checkbox"
                checked={hasAgreed || false}
                onChange={(e) => setHasAgreed(e.target.checked)}
                className={`h-4 w-4 focus:ring-blue-500 border rounded cursor-pointer ${
                  darkMode
                    ? 'text-blue-600 border-gray-600 bg-gray-800'
                    : 'text-blue-600 border-gray-300'
                }`}
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor={`${fieldId}-agreement`} className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                I have read and agree to the terms of the Non-Disclosure Agreement*
              </label>
            </div>
          </div>

          <div className="flex justify-between mt-4">
            <button
              type="button"
              onClick={handlePrevious}
              disabled={currentInputIndex === 0}
              className={`px-4 py-2 rounded-md font-medium ${
                currentInputIndex === 0
                  ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              Previous
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                handleSubmit(e);
              }}
              disabled={!hasAgreed || isSubmitting}
              className={`px-5 py-2 rounded-md font-medium flex items-center gap-2 ${
                !hasAgreed || isSubmitting
                  ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-[#1a3a6d] text-white hover:bg-[#14305e]'
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Redirecting to DocuSign…
                </>
              ) : (
                <>
                  <Shield className="h-4 w-4" />
                  Sign with DocuSign
                </>
              )}
            </button>
          </div>
        </div>
      );
    }
    
    return (
      <div className="mb-6 p-4 border rounded-lg bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600">
        <div className="mb-2">
          <label htmlFor={fieldId} className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {currentVariable.variableTitle}*
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
              ({currentInputIndex + 1} of {variablesInOrder.length})
            </span>
          </label>
        </div>
        
        {currentVariable.type === 'checkbox' ? (
          <div className="flex items-center">
            <input
              id={fieldId}
              type="checkbox"
              checked={currentValue === 'true' || currentValue === true}
              onChange={(e) => {
                handleChange(e.target.checked ? 'true' : 'false');
              }}
              className="mr-2"
            />
          </div>
        ) : currentVariable.variableTitle.toLowerCase().includes('business structure') || 
          currentVariable.variableTitle.toLowerCase().includes('structure') ? (
          <select
            id={fieldId}
            value={currentValue}
            onChange={(e) => {
              handleChange(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              darkMode 
                ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                : 'border-gray-300 text-gray-900 placeholder-gray-500'
            }`}
          >
            <option value="">Select Business Structure</option>
            <option value="corporation">Corporation</option>
            <option value="limited liability company">Limited Liability Company</option>
            <option value="partnership">Partnership</option>
            <option value="sole proprietorship">Sole Proprietorship</option>
            <option value="limited partnership">Limited Partnership</option>
          </select>
        ) : currentVariable.type === 'date' ? (
          <div className="flex gap-2">
            <input
              id={fieldId}
              type="date"
              value={currentValue}
              onChange={(e) => {
                handleChange(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              className={`flex-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                darkMode 
                  ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                  : 'border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
              autoFocus
            />
            <button
              type="button"
              onClick={() => {
                const today = new Date();
                const year = today.getFullYear();
                const month = String(today.getMonth() + 1).padStart(2, '0');
                const day = String(today.getDate()).padStart(2, '0');
                const todayString = `${year}-${month}-${day}`;
                handleChange(todayString);
                // Automatically advance to next input after setting today's date
                setTimeout(() => {
                  if (currentInputIndex < variablesInOrder.length - 1) {
                    handleNext();
                  }
                }, 100);
              }}
              className={`px-4 py-2 rounded-md font-medium text-sm whitespace-nowrap ${
                darkMode
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              Today
            </button>
          </div>
        ) : currentVariable.variableTitle.toLowerCase() === 'initials' ? (
          <div className="flex gap-2">
            <input
              id={fieldId}
              type="text"
              value={currentValue}
              onChange={(e) => {
                handleChange(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              placeholder={cleanPlaceholderText(currentVariable.content)}
              className={`flex-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                darkMode 
                  ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                  : 'border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
              autoFocus
            />
            {(() => {
              // Find the Initial variable (not Initials) to copy from
              const initialVariable = variablesInOrder.find(v => 
                v.variableTitle.toLowerCase() === 'initial' && 
                v.originalContent && 
                v.originalContent.toUpperCase() === 'INITIAL'
              );
              const initialValue = initialVariable ? dynamicFormData[initialVariable.id] : null;
              
              return initialValue ? (
                <button
                  type="button"
                  onClick={() => {
                    handleChange(initialValue);
                    // Automatically advance to next input after copying
                    setTimeout(() => {
                      if (currentInputIndex < variablesInOrder.length - 1) {
                        handleNext();
                      }
                    }, 100);
                  }}
                  className={`px-4 py-2 rounded-md font-medium text-sm whitespace-nowrap ${
                    darkMode
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  Use Initial ({initialValue})
                </button>
              ) : null;
            })()}
          </div>
        ) : (
          <input
            id={fieldId}
            type="text"
            value={currentValue}
            onChange={(e) => {
              handleChange(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            placeholder={cleanPlaceholderText(currentVariable.content)}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              darkMode 
                ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                : 'border-gray-300 text-gray-900 placeholder-gray-500'
            }`}
            autoFocus
          />
        )}
        
        <div className="flex justify-between mt-4">
          <button
            type="button"
            onClick={handlePrevious}
            disabled={currentInputIndex === 0}
            className={`px-4 py-2 rounded-md font-medium ${
              currentInputIndex === 0
                ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            Previous
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={currentInputIndex >= variablesInOrder.length - 1 || !hasValue()}
            className={`px-4 py-2 rounded-md font-medium ${
              currentInputIndex >= variablesInOrder.length - 1 || !hasValue()
                ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                : 'bg-red-600 text-white hover:bg-red-700'
            }`}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // Render grouped client input field
  const renderGroupedClientInputField = (group) => {
    const { primaryVariable, variables } = group;
    const fieldId = `client-field-${primaryVariable.id}`;
    
    // Debug logging for initials and signatures
    if (primaryVariable.type === 'initial' || primaryVariable.type === 'signature') {
      console.log(`Rendering ${primaryVariable.type} field:`, {
        primaryVariable,
        variables,
        dynamicFormData,
        currentValue: dynamicFormData[primaryVariable.id]
      });
    }
    
    // Use the value from the first variable in the group, or get from any variable that has a value
    const currentValue = variables.find(v => dynamicFormData[v.id])?.id 
      ? dynamicFormData[variables.find(v => dynamicFormData[v.id]).id]
      : dynamicFormData[primaryVariable.id] || '';
    
    // Update all variables in the group when the input changes
    const handleChange = (value) => {
      const updates = {};
      variables.forEach(variable => {
        updates[variable.id] = value;
      });
      
      // Debug logging for all field updates
      console.log(`Updating ${primaryVariable.type} field (${primaryVariable.variableTitle}):`, {
        value,
        updates,
        variables,
        variableAssignments: variables.map(v => ({ id: v.id, assignedTo: v.assignedTo, type: v.type, variableTitle: v.variableTitle }))
      });
      
      setDynamicFormData(prev => ({ ...prev, ...updates }));
      
      // Also update legacy formData for all field types to ensure document rendering and saving works
      const title = primaryVariable.variableTitle.toLowerCase();
      
      // Map to legacy form fields based on variable title content
      let legacyFieldUpdated = false;
      
      if (primaryVariable.type === 'initial') {
        setFormData(prev => ({ ...prev, initial: value }));
        console.log('Updated legacy formData.initial:', value);
        legacyFieldUpdated = true;
      } else if (primaryVariable.type === 'signature') {
        setFormData(prev => ({ ...prev, signature: value }));
        console.log('Updated legacy formData.signature:', value);
        legacyFieldUpdated = true;
      } else if (title.includes('signer') && title.includes('name')) {
        setFormData(prev => ({ ...prev, signerName: value }));
        console.log('Updated legacy formData.signerName:', value);
        legacyFieldUpdated = true;
      } else if (title.includes('signer') && title.includes('title')) {
        setFormData(prev => ({ ...prev, signerTitle: value }));
        console.log('Updated legacy formData.signerTitle:', value);
        legacyFieldUpdated = true;
      } else if (title.includes('company') && title.includes('name')) {
        setFormData(prev => ({ ...prev, companyName: value }));
        console.log('Updated legacy formData.companyName:', value);
        legacyFieldUpdated = true;
      } else if (title.includes('company') && title.includes('address')) {
        setFormData(prev => ({ ...prev, companyAddress: value }));
        console.log('Updated legacy formData.companyAddress:', value);
        legacyFieldUpdated = true;
      } else if (title.includes('city')) {
        setFormData(prev => ({ ...prev, city: value }));
        console.log('Updated legacy formData.city:', value);
        legacyFieldUpdated = true;
      } else if (title.includes('state')) {
        setFormData(prev => ({ ...prev, state: value }));
        console.log('Updated legacy formData.state:', value);
        legacyFieldUpdated = true;
      } else if (title.includes('zip')) {
        setFormData(prev => ({ ...prev, zipCode: value }));
        console.log('Updated legacy formData.zipCode:', value);
        legacyFieldUpdated = true;
      } else if (title.includes('business') && title.includes('structure')) {
        setFormData(prev => ({ ...prev, businessStructure: value }));
        console.log('Updated legacy formData.businessStructure:', value);
        legacyFieldUpdated = true;
      } else if (title.includes('name') && !title.includes('company')) {
        // Fallback for name fields that aren't company names
        setFormData(prev => ({ ...prev, signerName: value }));
        console.log('Updated legacy formData.signerName (fallback):', value);
        legacyFieldUpdated = true;
      } else if (title.includes('address')) {
        // Fallback for address fields
        setFormData(prev => ({ ...prev, companyAddress: value }));
        console.log('Updated legacy formData.companyAddress (fallback):', value);
        legacyFieldUpdated = true;
      } else if (title.includes('title') || title.includes('position')) {
        // Fallback for title/position fields
        setFormData(prev => ({ ...prev, signerTitle: value }));
        console.log('Updated legacy formData.signerTitle (fallback):', value);
        legacyFieldUpdated = true;
      }
      
      if (!legacyFieldUpdated) {
        console.log('No legacy form field mapping found for:', primaryVariable.variableTitle, primaryVariable.type);
      }
      
      // Update legacy formData for original template fields
      if (primaryVariable.isOriginalTemplate && primaryVariable.originalField) {
        const fieldId = primaryVariable.originalField.id;
        setFormData(prev => ({
          ...prev,
          [fieldId]: value
        }));
      }
    };
    
    return (
      <div key={primaryVariable.id} className="mb-4 w-full md:w-1/2 pr-2">
        <label htmlFor={fieldId} className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          {primaryVariable.variableTitle}*
          {variables.length > 1 && (
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
              ({variables.length} fields)
            </span>
          )}
        </label>
        
        {primaryVariable.type === 'checkbox' ? (
          <div className="flex items-center">
            <input
              id={fieldId}
              type="checkbox"
              checked={currentValue === 'true' || currentValue === true}
              onChange={(e) => {
                handleChange(e.target.checked ? 'true' : 'false');
              }}
              onFocus={() => {
                scrollToVariable(primaryVariable);
              }}
              onClick={() => {
                scrollToVariable(primaryVariable);
              }}
              className="mr-2"
              disabled={!isScrolledToBottom}
            />
          </div>
        ) : primaryVariable.variableTitle.toLowerCase().includes('business structure') || 
          primaryVariable.variableTitle.toLowerCase().includes('structure') ? (
          <select
            id={fieldId}
            value={currentValue}
            onChange={(e) => {
              handleChange(e.target.value);
            }}
            onFocus={() => {
              scrollToVariable(primaryVariable);
            }}
            onClick={() => {
              scrollToVariable(primaryVariable);
            }}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              darkMode 
                ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                : 'border-gray-300 text-gray-900 placeholder-gray-500'
            }`}
            disabled={!isScrolledToBottom}
          >
            <option value="">Select Business Structure</option>
            <option value="corporation">Corporation</option>
            <option value="limited liability company">Limited Liability Company</option>
            <option value="partnership">Partnership</option>
            <option value="sole proprietorship">Sole Proprietorship</option>
            <option value="limited partnership">Limited Partnership</option>
          </select>
        ) : (
          <input
            id={fieldId}
            type={primaryVariable.type === 'date' ? 'date' : 'text'}
            value={currentValue}
            onChange={(e) => {
              handleChange(e.target.value);
            }}
            onFocus={() => {
              scrollToVariable(primaryVariable);
            }}
            onClick={() => {
              scrollToVariable(primaryVariable);
            }}
            placeholder={cleanPlaceholderText(primaryVariable.content)}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              darkMode 
                ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                : 'border-gray-300 text-gray-900 placeholder-gray-500'
            }`}
            disabled={!isScrolledToBottom}
          />
        )}
      </div>
    );
  };

  // Render template element
  const renderTemplateElement = (element) => {
    // Debug logging for all elements
    if (element.content && element.content.includes('initial')) {
      console.log('Rendering element with initials:', {
        element,
        content: element.content,
        renderedContent: renderContentWithVariables(element.content, element.id)
      });
    }
    
    if (element.type === 'line') {
      return <div key={element.id} data-element-id={element.id} style={element.style}></div>;
    }
    
    if (element.type === 'header') {
      return (
        <h2 
          key={element.id} 
          data-element-id={element.id}
          style={element.style} 
          className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}
        >
          <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderContentWithVariables(element.content, element.id)) }} />
        </h2>
      );
    }
    
    if (element.type === 'text') {
      return (
        <div 
          key={element.id} 
          data-element-id={element.id}
          style={element.style} 
          className="mb-3"
        >
          <p dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderContentWithVariables(element.content, element.id)) }} />
          {/* Render sub-items if they exist */}
          {(element.subItems || []).length > 0 && (
            <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-300 ml-4 mt-2">
              {(element.subItems || []).map((subItem) => (
                <li key={subItem.id} className="text-sm">
                  <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderContentWithVariables(subItem.text || 'Empty sub-item', element.id)) }} />
                  {(subItem.subItems || []).length > 0 && (
                    <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                      {(subItem.subItems || []).map((nestedSubItem) => (
                        <li key={nestedSubItem.id} className="text-sm text-gray-600 dark:text-gray-300">
                          <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderContentWithVariables(nestedSubItem.text || 'Empty nested sub-item', element.id)) }} />
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    }
    
    // For input field types assigned to BBA, don't render as form fields
    if (['signature', 'initial', 'text_input', 'checkbox', 'date'].includes(element.type)) {
      if (element.assignedTo === 'BBA') {
        // Show as read-only text for BBA fields
        return (
          <div key={element.id} data-element-id={element.id} className="mb-3">
            <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderContentWithVariables(element.content, element.id)) }} />
          </div>
        );
      }
      // Client fields will be rendered separately in the form section
      return null;
    }
    
    // Handle any other element types that might contain variables
    if (element.content) {
      return (
        <div key={element.id} data-element-id={element.id} className="mb-3">
          <p dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderContentWithVariables(element.content, element.id)) }} />
        </div>
      );
    }
    
    return null;
  };

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <>
      <div className="min-h-screen w-full flex relative">
        {/* Same ambient BBA background the Start Dashboard shows */}
        <PortalBackground />
        <StartSidebar />
        <div className="relative flex-1 max-w-4xl mx-auto p-4">
        
        {/* Audit Trail Viewer Modal */}
        {showAuditTrail && (
          <AuditTrailViewer
            auditData={auditTrailData}
            onClose={() => setShowAuditTrail(false)}
          />
        )}
        <div className="mb-6 text-center">
          <img
            src="/bba-emblem.svg"
            alt="Builders Breakthrough Advisors"
            className="h-16 w-auto mx-auto mb-4"
          />
          <h1 className={`text-2xl font-bold mb-1 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Non-Disclosure Agreement</h1>
          <p className={`text-sm max-w-lg mx-auto ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Complete your information below, then you'll be taken to DocuSign to review and sign the NDA.
          </p>
        </div>

        {/* Frosted panel so the bare form fields stay legible over the ambient background */}
        <div className="rounded-2xl border border-gray-200 bg-white/85 p-5 shadow-sm backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800/85 sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {showValidation && (error || Object.keys(fieldErrors).length > 0) && (
              <div
                role="alert"
                className={`p-3 rounded-lg border text-sm flex gap-2 ${
                  darkMode ? 'bg-red-900/20 border-red-700 text-red-300' : 'bg-red-50 border-red-200 text-red-800'
                }`}
              >
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>
                  {error || 'Please fix the highlighted fields below.'}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

              {/* Full Name */}
              <div>
                <label htmlFor="nda-signerName" className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Full Name *</label>
                <input
                  id="nda-signerName"
                  type="text"
                  value={formData.signerName}
                  onChange={(e) => {
                    clearFieldError('signerName');
                    setFormData(prev => ({ ...prev, signerName: e.target.value }));
                  }}
                  placeholder="Jane Smith"
                  aria-invalid={Boolean(fieldErrors.signerName)}
                  aria-describedby={fieldErrors.signerName ? 'nda-signerName-error' : undefined}
                  className={ndaInputClass('signerName')}
                />
                {showValidation && fieldErrors.signerName && (
                  <p id="nda-signerName-error" className="mt-1 text-xs text-red-600 dark:text-red-400">{fieldErrors.signerName}</p>
                )}
              </div>

              {/* Title */}
              <div>
                <label htmlFor="nda-signerTitle" className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Title *</label>
                <input
                  id="nda-signerTitle"
                  type="text"
                  value={formData.signerTitle}
                  onChange={(e) => {
                    clearFieldError('signerTitle');
                    setFormData(prev => ({ ...prev, signerTitle: e.target.value }));
                  }}
                  placeholder="Owner"
                  aria-invalid={Boolean(fieldErrors.signerTitle)}
                  aria-describedby={fieldErrors.signerTitle ? 'nda-signerTitle-error' : undefined}
                  className={ndaInputClass('signerTitle')}
                />
                {showValidation && fieldErrors.signerTitle && (
                  <p id="nda-signerTitle-error" className="mt-1 text-xs text-red-600 dark:text-red-400">{fieldErrors.signerTitle}</p>
                )}
              </div>

              {/* Company Name */}
              <div>
                <label htmlFor="nda-companyName" className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Company Name *</label>
                <input
                  id="nda-companyName"
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => {
                    clearFieldError('companyName');
                    setFormData(prev => ({ ...prev, companyName: e.target.value }));
                  }}
                  placeholder="Acme Construction LLC"
                  aria-invalid={Boolean(fieldErrors.companyName)}
                  aria-describedby={fieldErrors.companyName ? 'nda-companyName-error' : undefined}
                  className={ndaInputClass('companyName')}
                />
                {showValidation && fieldErrors.companyName && (
                  <p id="nda-companyName-error" className="mt-1 text-xs text-red-600 dark:text-red-400">{fieldErrors.companyName}</p>
                )}
              </div>

              {/* Business Structure */}
              <div>
                <label htmlFor="nda-businessStructure" className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Business Structure *</label>
                <select
                  id="nda-businessStructure"
                  value={formData.businessStructure}
                  onChange={(e) => {
                    clearFieldError('businessStructure');
                    setFormData(prev => ({ ...prev, businessStructure: e.target.value }));
                  }}
                  aria-invalid={Boolean(fieldErrors.businessStructure)}
                  aria-describedby={fieldErrors.businessStructure ? 'nda-businessStructure-error' : undefined}
                  className={ndaInputClass('businessStructure')}
                >
                  <option value="">Select…</option>
                  {NDA_BUSINESS_STRUCTURE_OPTIONS.map((label) => (
                    <option key={label} value={label}>{label}</option>
                  ))}
                </select>
                {showValidation && fieldErrors.businessStructure && (
                  <p id="nda-businessStructure-error" className="mt-1 text-xs text-red-600 dark:text-red-400">{fieldErrors.businessStructure}</p>
                )}
              </div>

              {/* Street */}
              <div>
                <label htmlFor="nda-companyAddress" className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Street *</label>
                <input
                  id="nda-companyAddress"
                  type="text"
                  value={formData.companyAddress}
                  onChange={(e) => {
                    clearFieldError('companyAddress');
                    setFormData(prev => ({ ...prev, companyAddress: e.target.value }));
                  }}
                  placeholder="123 Main St"
                  aria-invalid={Boolean(fieldErrors.companyAddress)}
                  aria-describedby={fieldErrors.companyAddress ? 'nda-companyAddress-error' : undefined}
                  className={ndaInputClass('companyAddress')}
                />
                {showValidation && fieldErrors.companyAddress && (
                  <p id="nda-companyAddress-error" className="mt-1 text-xs text-red-600 dark:text-red-400">{fieldErrors.companyAddress}</p>
                )}
              </div>

              {/* City */}
              <div>
                <label htmlFor="nda-city" className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>City *</label>
                <input
                  id="nda-city"
                  type="text"
                  value={formData.city}
                  onChange={(e) => {
                    clearFieldError('city');
                    setFormData(prev => ({ ...prev, city: e.target.value }));
                  }}
                  placeholder="Charlotte"
                  aria-invalid={Boolean(fieldErrors.city)}
                  aria-describedby={fieldErrors.city ? 'nda-city-error' : undefined}
                  className={ndaInputClass('city')}
                />
                {showValidation && fieldErrors.city && (
                  <p id="nda-city-error" className="mt-1 text-xs text-red-600 dark:text-red-400">{fieldErrors.city}</p>
                )}
              </div>

              {/* State */}
              <div>
                <label htmlFor="nda-state" className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>State *</label>
                <input
                  id="nda-state"
                  type="text"
                  value={formData.state}
                  onChange={(e) => {
                    clearFieldError('state');
                    setFormData(prev => ({ ...prev, state: e.target.value }));
                  }}
                  placeholder="NC"
                  maxLength={2}
                  aria-invalid={Boolean(fieldErrors.state)}
                  aria-describedby={fieldErrors.state ? 'nda-state-error' : undefined}
                  className={ndaInputClass('state')}
                />
                {showValidation && fieldErrors.state && (
                  <p id="nda-state-error" className="mt-1 text-xs text-red-600 dark:text-red-400">{fieldErrors.state}</p>
                )}
              </div>

              {/* Zip Code */}
              <div>
                <label htmlFor="nda-zipCode" className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Zip Code *</label>
                <input
                  id="nda-zipCode"
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => {
                    clearFieldError('zipCode');
                    setFormData(prev => ({ ...prev, zipCode: e.target.value }));
                  }}
                  placeholder="28201"
                  aria-invalid={Boolean(fieldErrors.zipCode)}
                  aria-describedby={fieldErrors.zipCode ? 'nda-zipCode-error' : undefined}
                  className={ndaInputClass('zipCode')}
                />
                {showValidation && fieldErrors.zipCode && (
                  <p id="nda-zipCode-error" className="mt-1 text-xs text-red-600 dark:text-red-400">{fieldErrors.zipCode}</p>
                )}
              </div>
            </div>

            <div className="relative w-full">
              <button
                type="submit"
                disabled={!isFormComplete || isSubmitting}
                className={`w-full py-3 px-6 rounded-md font-medium flex items-center justify-center gap-2 transition-colors ${
                  isSubmitting || !isFormComplete
                    ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                    : 'bg-[#1a3a6d] text-white hover:bg-[#14305e]'
                }`}
              >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Redirecting to DocuSign…
                </>
              ) : (
                <>
                  <Shield className="h-4 w-4" />
                  Sign NDA with DocuSign
                </>
              )}
              </button>
              {!isFormComplete && !isSubmitting && (
                <button
                  type="button"
                  className="absolute inset-0 z-10 w-full rounded-md cursor-not-allowed"
                  onClick={handleEarlySignClick}
                  aria-label="Complete all required fields to sign the NDA"
                />
              )}
            </div>
          </form>
        </div>
        </div>
      </div>
    </>
  );
};

export default NDASigningPage;